import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import SubscriptionSuccess from "./SubscriptionSuccess";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  BarChart3,
  Building2,
  Wallet,
  Ruler,
  PieChart as PieChartIcon,
  Calendar,
  FileText,
  TrendingUp,
  CheckCircle2,
  Clock,
  MapPin,
  Loader2,
  ArrowUpDown,
  ChevronDown,
  DollarSign,
  Search,
  Activity,
  CalendarCheck,
  AlertTriangle,
  Target,
  Info,
  TrendingDown,
  Users,
  User,
  Package,
} from "lucide-react";
import { obtenerPermisosCliente, puedeAccederAVista } from "./permisos";
import {
  calcularConSistemaActualizacionPorcentajeAdicionales as calcularConSistemaActualizacionPorcentajeAdicionalesShared,
  formatearFechaParaIndice as formatearFechaParaIndiceShared,
  formatearFechaSinTimezone as formatearFechaSinTimezoneShared,
  agruparPagosPorMes as agruparPagosPorMesShared,
  convertirMonto as convertirMontoShared,
} from "./utils/calculosHonorarios";
import StandardModal from "./StandardModal";
import { trackCreate, trackUserAction } from "./hooks/useAnalytics";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA DASHBOARD
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
  solid: "#0ea5e9",
  light: "#0ea5e910",
  description: "Vista general del sistema",
});

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente Toggle para monedas
const CurrencyToggle = ({ value, onChange }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#f8fafc",
        borderRadius: "12px",
        padding: "4px",
        border: "1px solid #e2e8f0",
      }}
    >
      {["ARS", "USD"].map((currency) => (
        <button
          key={currency}
          onClick={() => onChange(currency)}
          className="button-hover"
          style={{
            padding: "8px 16px",
            border: "none",
            borderRadius: "8px",
            background:
              value === currency
                ? "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)"
                : "transparent",
            color: value === currency ? "white" : "#64748b",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: "60px",
            justifyContent: "center",
            touchAction: "manipulation",
          }}
        >
          <DollarSign
            size={14}
            color={value === currency ? "white" : "#64748b"}
          />
          {currency}
        </button>
      ))}
    </div>
  );
};

// **NUEVO** Componente Toggle para la vista de costo (Total / m²)
const CostViewToggle = ({ value, onChange }) => {
  return (
    <div
      style={{
        display: "flex",
        background: "#f1f5f9",
        borderRadius: "8px",
        padding: "3px",
      }}
    >
      <button
        onClick={() => onChange("total")}
        style={{
          padding: "4px 10px",
          border: "none",
          borderRadius: "6px",
          background: value === "total" ? "white" : "transparent",
          color: value === "total" ? "#3b82f6" : "#64748b",
          fontSize: "12px",
          fontWeight: "600",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: value === "total" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
        }}
      >
        Total
      </button>
      <button
        onClick={() => onChange("m2")}
        style={{
          padding: "4px 10px",
          border: "none",
          borderRadius: "6px",
          background: value === "m2" ? "white" : "transparent",
          color: value === "m2" ? "#3b82f6" : "#64748b",
          fontSize: "12px",
          fontWeight: "600",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: value === "m2" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
        }}
      >
        por m²
      </button>
    </div>
  );
};

const ModernGaugeChart = ({
  costoActualTotal, // **NUEVO**
  costoActualPorMetro,
  presupuestoInicialTotal, // **NUEVO**
  presupuestoInicialPorMetro,
  presupuestoActualizadoTotal, // **NUEVO**
  presupuestoActualizadoPorMetro,
  metrosComputo,
  formatCurrency,
  monedaVista,
  isMobile,
  datosCompletamenteCargados, // ✅ NUEVO: Para controlar animación
}) => {
  // **NUEVO** Estado para controlar la vista
  const [costoVista, setCostoVista] = useState("total"); // 'm2' o 'total'

  // Mostrar placeholder si los datos no están listos
  const datosListos = costoActualTotal > 0 && presupuestoActualizadoTotal > 0;
  const [showInfo, setShowInfo] = useState(false);
  const [animatedValue, setAnimatedValue] = useState(0);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  // 🔍 DEBUG: Log de valores para diagnosticar (solo cuando cambian los datos)
  useEffect(() => {
    if (datosListos && datosCompletamenteCargados) {
      console.log("🔍 DEBUG - Widget Costo vs Presupuesto:", {
        costoActualTotal,
        presupuestoActualizadoTotal,
        datosListos,
        datosCompletamenteCargados,
        datosCompletamenteListos: datosListos && datosCompletamenteCargados,
        // Diagnóstico específico
        costoActualEsCero: costoActualTotal === 0,
        presupuestoActualizadoEsCero: presupuestoActualizadoTotal === 0,
        datosCompletamenteCargadosEsFalse: datosCompletamenteCargados === false
      });
    }
  }, [costoActualTotal, presupuestoActualizadoTotal, datosCompletamenteCargados]);

  // ✅ CONDICIÓN MEJORADA: Mostrar gauge cuando TODOS los datos estén completamente listos
  const datosCompletamenteListos = datosListos && datosCompletamenteCargados;
  
  // ✅ SOLUCIÓN ELEGANTE: Siempre mostrar widget, pero solo animar cuando los datos estén listos
  const mostrarWidget = true; // Siempre mostrar el widget
  const animarWidget = datosCompletamenteListos; // Solo animar cuando los datos estén listos

  // **MODIFICADO** Seleccionar valores según la vista
  const costoActual =
    costoVista === "m2" ? costoActualPorMetro : costoActualTotal;
  const presupuestoInicial =
    costoVista === "m2" ? presupuestoInicialPorMetro : presupuestoInicialTotal;
  const presupuestoActualizado =
    costoVista === "m2"
      ? presupuestoActualizadoPorMetro
      : presupuestoActualizadoTotal;

  // ✅ CALCULAR VALORES CORRECTAMENTE - SIN MAXVALUE CONFUSO
  const progresoVsActualizado =
    presupuestoActualizado > 0
      ? (costoActual / presupuestoActualizado) * 100
      : 0;

  // ✅ PORCENTAJES PARA EL GAUGE (0-100% del semicírculo)
  const porcentajeActual = Math.min(progresoVsActualizado, 100); // ✅ REAL vs presupuesto

  // ✅ MARCADORES: Solo si hay presupuestos
  const porcentajeInicial =
    presupuestoInicial > 0 && presupuestoActualizado > 0
      ? Math.min((presupuestoInicial / presupuestoActualizado) * 100, 100)
      : 0;

  const porcentajeActualizado = 100; // ✅ SIEMPRE 100% (es la referencia)

  // ✅ ANIMACIÓN DEL ARCO VERDE: Crecer de 0 al valor final
  useEffect(() => {
    // Solo animar cuando TODOS los datos estén completamente listos
    if (
      costoActual > 0 &&
      presupuestoActualizado > 0 &&
      animarWidget
    ) {
      // Resetear valores para asegurar que empiece de 0
      setAnimatedValue(0);
      setAnimatedPercentage(0);

      // Animar inmediatamente sin delay
      let frame;
      let start;

      const animate = (time) => {
        if (!start) start = time;
        const progress = Math.min((time - start) / 1500, 1);
        const smooth = 1 - Math.pow(1 - progress, 3);

        setAnimatedValue(costoActual * smooth);
        setAnimatedPercentage(porcentajeActual * smooth);

        if (progress < 1) {
          frame = requestAnimationFrame(animate);
        }
      };

      frame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(frame);
    }
  }, [
    costoActual,
    presupuestoActualizado,
    costoVista,
    animarWidget,
  ]); // ✅ AGREGADO animarWidget

  // ✅ FUNCIÓN CORREGIDA: Crear path del arco
  const createArcPath = (percentage) => {
    const radius = 70;
    const centerX = 85;
    const centerY = 85;

    // Convertir porcentaje a ángulos (0-180°)
    const angle = (percentage / 100) * 180;

    // Ángulos en radianes (180° = π, 0° = 0)
    const startRadians = Math.PI; // 180° (izquierda)
    const endRadians = Math.PI - (angle * Math.PI) / 180; // Hacia la derecha

    // Puntos del arco
    const startX = centerX + radius * Math.cos(startRadians);
    const startY = centerY - radius * Math.sin(startRadians);
    const endX = centerX + radius * Math.cos(endRadians);
    const endY = centerY - radius * Math.sin(endRadians);

    // Determinar si es arco grande (>180°) - nunca en semicírculo
    const largeArcFlag = 0;
    const sweepFlag = 1; // Sentido horario

    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${endX} ${endY}`;
  };

  // ✅ FUNCIÓN CORREGIDA: Posición de marcadores
  const getMarkerPosition = (percentage) => {
    const radius = 70;
    const centerX = 85;
    const centerY = 85;

    // Mismo cálculo que el arco
    const angle = (percentage / 100) * 180;
    const radians = Math.PI - (angle * Math.PI) / 180;

    return {
      x: centerX + radius * Math.cos(radians),
      y: centerY - radius * Math.sin(radians),
    };
  };

  // ✅ COLOR FIJO: Azul siempre (consistencia visual en todos los proyectos)
  const getGaugeColor = () => {
    return "#3b82f6"; // Azul
  };

  // ✅ SIEMPRE RENDERIZAR LA TARJETA - NUNCA RETURN NULL
  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        padding: isMobile ? "16px" : "20px",
        border: "1px solid #f1f5f9",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
      className="card-hover"
    >
      {/* **MODIFICADO** Header con título dinámico y toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "#f59e0b10",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Target size={16} color="#f59e0b" />
          </div>
          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Costo actual vs. Presupuesto
            </h3>
            <p
              style={{
                fontSize: "12px",
                color: "#64748b",
                margin: "2px 0 0 0",
                fontStyle: "italic",
              }}
            >
              sin adicionales
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CostViewToggle value={costoVista} onChange={setCostoVista} />
          <button
            onClick={() => animarWidget && setShowInfo(!showInfo)}
            style={{
              background: animarWidget ? "#f1f5f9" : "#f8fafc",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              cursor: animarWidget ? "pointer" : "not-allowed",
              color: animarWidget ? "#64748b" : "#cbd5e1",
              transition: "all 0.2s ease",
              touchAction: "manipulation",
            }}
            onMouseEnter={(e) => {
              if (animarWidget) {
                e.target.style.background = "#e2e8f0";
                e.target.style.color = "#3b82f6";
              }
            }}
            onMouseLeave={(e) => {
              if (animarWidget) {
                e.target.style.background = "#f1f5f9";
                e.target.style.color = "#64748b";
              }
            }}
          >
            <Info size={16} />
          </button>
        </div>
      </div>

      {/* ✅ CONTENIDO CONDICIONAL: Gauge o Placeholder */}
      {mostrarWidget ? (
        <>
          {/* ✅ SVG GAUGE CORREGIDO */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <div
              style={{ position: "relative", width: "200px", height: "120px" }}
            >
              <svg
                width="200"
                height="120"
                viewBox="0 0 170 95"
                style={{ overflow: "visible" }}
              >
                {/* ✅ ARCO DE FONDO CONSISTENTE */}
                <path
                  d={createArcPath(100)}
                  fill="none"
                  stroke="#f1f5f9"
                  strokeWidth="8"
                  strokeLinecap="round"
                />

                {/* ✅ ARCO PRINCIPAL ANIMADO - Solo cuando esté listo */}
                {animarWidget && (
                  <path
                    d={createArcPath(animatedPercentage)}
                    fill="none"
                    stroke={getGaugeColor()}
                    strokeWidth="8"
                    strokeLinecap="round"
                    style={{
                      filter: `drop-shadow(0 0 8px ${getGaugeColor()}40)`,
                      transition: "stroke 0.3s ease",
                    }}
                  />
                )}

                {/* ✅ MARCADOR ANIMADO DEL VALOR ACTUAL - Solo cuando esté listo */}
                {animarWidget &&
                  (() => {
                    const pos = getMarkerPosition(animatedPercentage);
                    return (
                      <g key="marcador-actual">
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r="6"
                          fill={getGaugeColor()}
                          stroke="#fff"
                          strokeWidth="2"
                          style={{
                            filter: `drop-shadow(0 0 6px ${getGaugeColor()}60)`,
                          }}
                        />
                      </g>
                    );
                  })()}

                {/* ✅ MARCADOR PRESUPUESTO INICIAL */}
                {presupuestoInicial > 0 &&
                  (() => {
                    const pos = getMarkerPosition(porcentajeInicial);
                    return (
                      <g key="marcador-inicial">
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r="4"
                          fill="white"
                          stroke="#93c5fd"
                          strokeWidth="2"
                        />
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r="8"
                          fill="none"
                          stroke="#93c5fd"
                          strokeWidth="1"
                          opacity="0.3"
                        />
                      </g>
                    );
                  })()}

                {/* ✅ MARCADOR PRESUPUESTO ACTUALIZADO */}
                {presupuestoActualizado > 0 &&
                  (() => {
                    const pos = getMarkerPosition(porcentajeActualizado);
                    return (
                      <g key="marcador-actualizado">
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r="4"
                          fill="white"
                          stroke="#3b82f6"
                          strokeWidth="2"
                        />
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r="8"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="1"
                          opacity="0.3"
                        />
                      </g>
                    );
                  })()}
              </svg>

              {/* **MODIFICADO** VALOR CENTRAL */}
              <div
                style={{
                  position: "absolute",
                  top: "65px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "18px" : "22px",
                    fontWeight: "800",
                    color: getGaugeColor(),
                    marginBottom: "2px",
                    letterSpacing: "-0.5px",
                    fontVariantNumeric: "tabular-nums", // Números de ancho fijo
                  }}
                >
                  {formatCurrency(Math.round(animarWidget ? animatedValue : costoActual))}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {costoVista === "m2" ? "por m²" : "Total"}
                </div>
              </div>
            </div>
          </div>

          {/* **MODIFICADO** Métricas de comparación */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "10px",
              marginBottom: "12px",
              width: "100%",
            }}
          >
            {/* Actual vs Actualizado */}
                          <div
                style={{
                  background: "#f8fafc",
                  padding: "10px",
                  borderRadius: "8px",
                  textAlign: "center",
                  minWidth: "0", // Evita overflow
                }}
              >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: getGaugeColor(),
                  marginBottom: "2px",
                  fontVariantNumeric: "tabular-nums", // Números de ancho fijo
                }}
              >
                {progresoVsActualizado.toFixed(1)}%
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                vs Actualizado
              </div>
            </div>

            {/* Presupuesto Inicial */}
            <div
              style={{
                background: presupuestoInicial > 0 ? "#f0f9ff" : "#f8fafc",
                padding: "10px",
                borderRadius: "8px",
                textAlign: "center",
                border:
                  presupuestoInicial > 0
                    ? "1px solid #93c5fd20"
                    : "1px solid #e2e8f0",
                minWidth: "0", // Evita overflow
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  marginBottom: "2px",
                }}
              >
                {presupuestoInicial > 0 && (
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#93c5fd",
                      border: "1px solid white",
                    }}
                  />
                )}
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: presupuestoInicial > 0 ? "#93c5fd" : "#64748b",
                    fontVariantNumeric: "tabular-nums", // Números de ancho fijo
                  }}
                >
                  {presupuestoInicial > 0
                    ? formatCurrency(presupuestoInicial)
                    : "N/A"}
                </div>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Inicial
              </div>
            </div>

            {/* Presupuesto Actualizado */}
            <div
              style={{
                background: presupuestoActualizado > 0 ? "#eff6ff" : "#f8fafc",
                padding: "10px",
                borderRadius: "8px",
                textAlign: "center",
                border:
                  presupuestoActualizado > 0
                    ? "1px solid #3b82f620"
                    : "1px solid #e2e8f0",
                minWidth: "0", // Evita overflow
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  marginBottom: "2px",
                }}
              >
                {presupuestoActualizado > 0 && (
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#3b82f6",
                      border: "1px solid white",
                    }}
                  />
                )}
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: presupuestoActualizado > 0 ? "#3b82f6" : "#64748b",
                    fontVariantNumeric: "tabular-nums", // Números de ancho fijo
                  }}
                >
                  {presupuestoActualizado > 0
                    ? formatCurrency(presupuestoActualizado)
                    : "N/A"}
                </div>
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Actualizado
              </div>
            </div>
          </div>

          {/* **MODIFICADO** Información base */}
          {costoVista === "m2" && (
            <div
              style={{
                textAlign: "center",
                fontSize: "12px",
                color: "#64748b",
                paddingTop: "12px",
                borderTop: "1px solid #f1f5f9",
                width: "100%",
              }}
            >
              Total invertido ÷ {metrosComputo || 0} m² de cómputo
            </div>
          )}
        </>
      ) : (
        // ✅ PLACEHOLDER ELEGANTE - GAUGE DE FONDO CON ANIMACIÓN SUAVE
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <div
            style={{ position: "relative", width: "200px", height: "120px" }}
          >
            <svg
              width="200"
              height="120"
              viewBox="0 0 170 95"
              style={{ overflow: "visible" }}
            >
              {/* Arco de fondo estático */}
              <path
                d="M 15 85 A 70 70 0 0 1 155 85"
                fill="none"
                stroke="#f1f5f9"
                strokeWidth="8"
                strokeLinecap="round"
              />
            </svg>

            {/* Valor central con shimmer */}
            <div
              style={{
                position: "absolute",
                top: "65px",
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "24px",
                  background:
                    "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite",
                  borderRadius: "6px",
                  marginBottom: "2px",
                }}
              />
              <div
                style={{
                  width: "40px",
                  height: "12px",
                  background:
                    "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite 0.3s",
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de información usando StandardModal */}
      <StandardModal
        isOpen={showInfo && datosCompletamenteListos}
        onClose={() => setShowInfo(false)}
        title="Costo Actual vs. Presupuesto"
        subtitle="¿Cómo interpretar este gráfico?"
        icon={Target}
        isMobile={isMobile}
        gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
        maxWidth="600px"
      >
        <div style={{ display: "grid", gap: "16px" }}>
          {/* Explicación del arco principal */}
          <div
            style={{
              background: "#fef3c7",
              padding: "16px",
              borderRadius: "12px",
              border: "1px solid #fbbf24",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  background: getGaugeColor(),
                  border: "2px solid white",
                  boxShadow: `0 0 0 2px ${getGaugeColor()}40`,
                }}
              />
              <strong style={{ color: "#92400e", fontSize: "14px" }}>
                Arco principal
              </strong>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "#92400e",
                lineHeight: "1.5",
              }}
            >
              Tu costo actual basado en lo que ya gastaste. El color cambia
              según qué tan cerca estés del presupuesto actualizado.
            </p>
          </div>

          {/* Marcadores de presupuesto actualizado */}
          <div style={{ display: "grid", gap: "12px" }}>
            {presupuestoInicial > 0 && (
              <div
                style={{
                  background: "#f0f9ff",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #93c5fd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: "white",
                      border: "2px solid #93c5fd",
                      boxShadow: "0 0 0 2px #93c5fd40",
                    }}
                  />
                  <strong style={{ color: "#1e40af", fontSize: "14px" }}>
                    Presupuesto inicial
                  </strong>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#1e40af",
                    lineHeight: "1.5",
                  }}
                >
                  Marca azul claro: Presupuesto inicial (valores originales sin
                  actualizaciones).
                </p>
              </div>
            )}

            {presupuestoActualizado > 0 && (
              <div
                style={{
                  background: "#eff6ff",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #3b82f6",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: "white",
                      border: "2px solid #3b82f6",
                      boxShadow: "0 0 0 2px #3b82f640",
                    }}
                  />
                  <strong style={{ color: "#1e3a8a", fontSize: "14px" }}>
                    Presupuesto actualizado
                  </strong>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#1e3a8a",
                    lineHeight: "1.5",
                  }}
                >
                  Marca azul: Presupuesto actualizado (con inflación CAC
                  aplicada).
                </p>
              </div>
            )}
          </div>

          {/* Información adicional */}
          <div
            style={{
              background: "#f8fafc",
              padding: "16px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <Info size={16} color="#64748b" />
              <strong style={{ color: "#374151", fontSize: "14px" }}>
                Información adicional
              </strong>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "#64748b",
                lineHeight: "1.5",
              }}
            >
              El cálculo se basa en el total invertido dividido por los{" "}
              {metrosComputo || 0} m² de cómputo del proyecto.
            </p>
          </div>
        </div>
      </StandardModal>
    </div>
  );
};

// El resto del archivo Dashboard.js permanece igual...
// ... (pegar el resto del archivo sin cambios)
// ✅ NUEVA TARJETA: Honorarios y Mano de Obra
const HonorariosManoObraCard = ({
  datosHonorarios,
  datosManoObra,
  formatCurrency,
  isMobile,
  proyectoSeleccionado,
  datosAdicionales, // ✅ NUEVO: Datos de adicionales
}) => {
  // ✅ VERIFICAR SI EL PROYECTO TIENE MANO DE OBRA SEPARADA
  const tieneManoObraSeparada =
    proyectoSeleccionado?.mano_obra_separada === true;

  // Calcular totales de honorarios
  const honorariosTotal = datosHonorarios
    ? (datosHonorarios.resumenFinal?.pagosAcumulados || 0) +
      (datosHonorarios.resumenFinal?.saldoPendiente || 0)
    : 0;
  const honorariosPagado = datosHonorarios?.resumenFinal?.pagosAcumulados || 0;
  const honorariosSaldo = datosHonorarios?.resumenFinal?.saldoPendiente || 0;
  const honorariosPorcentaje =
    honorariosTotal > 0 ? (honorariosPagado / honorariosTotal) * 100 : 0;

  // ✅ CALCULAR TOTALES DE MANO DE OBRA SOLO SI ESTÁ SEPARADA Y TIENE DATOS
  const manoObraTotal =
    tieneManoObraSeparada && datosManoObra
      ? (datosManoObra.resumenFinal?.pagosAcumulados || 0) +
        (datosManoObra.resumenFinal?.saldoPendiente || 0)
      : 0;
  const manoObraPagado =
    tieneManoObraSeparada && datosManoObra
      ? datosManoObra?.resumenFinal?.pagosAcumulados || 0
      : 0;
  const manoObraSaldo =
    tieneManoObraSeparada && datosManoObra
      ? datosManoObra?.resumenFinal?.saldoPendiente || 0
      : 0;
  const manoObraPorcentaje =
    manoObraTotal > 0 ? (manoObraPagado / manoObraTotal) * 100 : 0;

  console.log("🔍 DEBUG - Monedas en tarjeta:", {
    honorariosMoneda: datosHonorarios?.monedaInicial,
    manoObraMoneda: datosManoObra?.monedaInicial,
  });

  // ✅ CALCULAR TOTALES DE HONORARIOS ADICIONALES
  const honorariosAdicionalesTotal = datosAdicionales?.honorariosCalculados || 0;
  const honorariosAdicionalesPagado = datosAdicionales?.honorariosPagados || 0;
  const honorariosAdicionalesSaldo = datosAdicionales?.honorariosPendientes || 0;
  const honorariosAdicionalesPorcentaje = honorariosAdicionalesTotal > 0 ? (honorariosAdicionalesPagado / honorariosAdicionalesTotal) * 100 : 0;

  // ✅ DETERMINAR QUE MOSTRAR
  const mostrarManoObra = tieneManoObraSeparada && manoObraTotal > 0;
  // NO mostrar adicionales si honorarios son por porcentaje (ya están incluidos)
  const esHonorariosPorcentaje = proyectoSeleccionado?.tipo_obra === "administracion_porcentaje";
  const mostrarHonorariosAdicionales = !esHonorariosPorcentaje && datosAdicionales?.honorariosHabilitados && datosAdicionales?.tieneAdicionales && honorariosAdicionalesTotal > 0;

  // ✅ VALIDACIÓN ADICIONAL: Si no hay proyecto o datos son inválidos
  if (!proyectoSeleccionado || (honorariosTotal === 0 && !mostrarManoObra && !mostrarHonorariosAdicionales)) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          border: "1px solid #f1f5f9",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          textAlign: "center",
          color: "#64748b",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#f1f5f9",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <FileText size={24} style={{ opacity: 0.5 }} />
        </div>
        <h4
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "4px",
            fontSize: "14px",
          }}
        >
          Sin datos configurados
        </h4>
        <p
          style={{
            margin: 0,
            textAlign: "center",
            lineHeight: "1.4",
            fontSize: "12px",
          }}
        >
          Configure los montos en el proyecto
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        padding: isMobile ? "20px" : "24px",
        border: "1px solid #f1f5f9",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: "slideInRight 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      className="card-hover"
    >
      {/* Header dinámico */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            background: "#8b5cf610",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DollarSign size={16} color="#8b5cf6" />
        </div>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
            margin: 0,
          }}
        >
          {mostrarManoObra ? "Honorarios y Mano de Obra" : "Honorarios"}
        </h3>
      </div>

      {/* Contenido */}
      <div style={{ display: "grid", gap: mostrarManoObra ? "20px" : "0px" }}>
        {/* HONORARIOS */}
        {honorariosTotal > 0 && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <User size={14} color="#8b5cf6" />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Honorarios
                </h4>
              </div>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#8b5cf6",
                }}
              >
                {honorariosPorcentaje.toFixed(1)}%
              </span>
            </div>

            {/* Barra de progreso */}
            <div
              style={{
                width: "100%",
                height: "12px",
                background: "#f1f5f9",
                borderRadius: "20px",
                overflow: "hidden",
                marginBottom: "10px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${Math.min(honorariosPorcentaje, 100)}%`,
                  height: "100%",
                  background:
                    "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  borderRadius: "20px",
                  transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)",
                }}
              />
            </div>

            {/* Información detallada */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "12px" }}>
                <span style={{ color: "#059669", fontWeight: "600" }}>
                  Pagado:{" "}
                  {formatCurrency(
                    Math.round(honorariosPagado),
                    datosHonorarios?.monedaInicial
                  )}
                </span>
                <span style={{ color: "#dc2626", fontWeight: "600" }}>
                  Saldo:{" "}
                  {formatCurrency(
                    Math.round(honorariosSaldo),
                    datosHonorarios?.monedaInicial
                  )}
                </span>
              </div>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: "700",
                  fontSize: "13px",
                }}
              >
                Total:{" "}
                {formatCurrency(
                  Math.round(honorariosTotal),
                  datosHonorarios?.monedaInicial
                )}
              </span>
            </div>
          </div>
        )}

        {/* MANO DE OBRA - SOLO SI EXISTE Y TIENE DATOS */}
        {mostrarManoObra && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Users size={14} color="#06b6d4" />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Mano de Obra
                </h4>
              </div>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#06b6d4",
                }}
              >
                {manoObraPorcentaje.toFixed(1)}%
              </span>
            </div>

            {/* Barra de progreso */}
            <div
              style={{
                width: "100%",
                height: "12px",
                background: "#f1f5f9",
                borderRadius: "20px",
                overflow: "hidden",
                marginBottom: "10px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${Math.min(manoObraPorcentaje, 100)}%`,
                  height: "100%",
                  background:
                    "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                  borderRadius: "20px",
                  transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 2px 8px rgba(6, 182, 212, 0.3)",
                }}
              />
            </div>

            {/* Información detallada */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "12px" }}>
                <span style={{ color: "#059669", fontWeight: "600" }}>
                  Pagado:{" "}
                  {formatCurrency(Math.round(manoObraPagado), datosManoObra?.monedaInicial)}
                </span>
                <span style={{ color: "#dc2626", fontWeight: "600" }}>
                  Saldo:{" "}
                  {formatCurrency(Math.round(manoObraSaldo), datosManoObra?.monedaInicial)}
                </span>
              </div>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: "700",
                  fontSize: "13px",
                }}
              >
                Total:{" "}
                {formatCurrency(Math.round(manoObraTotal), datosManoObra?.monedaInicial)}
              </span>
            </div>
          </div>
        )}

        {/* HONORARIOS ADICIONALES - SOLO SI EXISTE Y TIENE DATOS */}
        {mostrarHonorariosAdicionales && (
          <div style={{ marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Package size={14} color="#f59e0b" />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Honorarios por Adicionales
                </h4>
              </div>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#f59e0b",
                }}
              >
                {honorariosAdicionalesPorcentaje.toFixed(1)}%
              </span>
            </div>

            {/* Barra de progreso */}
            <div
              style={{
                width: "100%",
                height: "8px",
                background: "#fef3cd",
                borderRadius: "4px",
                overflow: "hidden",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  width: `${Math.min(honorariosAdicionalesPorcentaje, 100)}%`,
                  height: "100%",
                  background: "#f59e0b",
                  borderRadius: "4px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>

            {/* Detalles */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "12px",
                gap: "12px",
              }}
            >
              <span
                style={{
                  color: "#22c55e",
                  fontWeight: "600",
                }}
              >
                Pagado: {formatCurrency(Math.round(honorariosAdicionalesPagado))}
              </span>
              <span
                style={{
                  color: "#f59e0b",
                  fontWeight: "600",
                }}
              >
                Saldo: {formatCurrency(Math.round(honorariosAdicionalesSaldo))}
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: "700",
                }}
              >
                Total: {formatCurrency(Math.round(honorariosAdicionalesTotal))}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ✅ COMPONENTE DONUT CHART CORREGIDO - PORCENTAJES CORRECTOS
const EstadoFinancieroChart = ({
  datosFinancieros,
  formatCurrency,
  isMobile,
  totalPresupuestadoActualizado = 0,
}) => {
  if (!datosFinancieros || datosFinancieros.length === 0) {
    return (
      <div
        style={{
          height: "280px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: "14px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#f1f5f9",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "12px",
          }}
        >
          <Target size={24} style={{ opacity: 0.5 }} />
        </div>
        <h4
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "4px",
            fontSize: "14px",
          }}
        >
          Sin datos de presupuestos
        </h4>
        <p
          style={{
            margin: 0,
            textAlign: "center",
            lineHeight: "1.4",
            fontSize: "12px",
          }}
        >
          Crea presupuestos iniciales para ver el estado financiero
        </p>
      </div>
    );
  }

  // ✅ USAR PRESUPUESTO ACTUALIZADO REAL (no sumar valores del gráfico)

  // ✅ CÁLCULO CORREGIDO: Todo lo gastado y comprometido (con los nombres actualizados)
  const gastadoYComprometido = datosFinancieros
    .filter(
      (item) =>
        item.name === "Pagado" ||
        item.name === "Comprometido Gastos" ||
        item.name === "Comprometido Hon." ||
        item.name === "Comprometido M.O." ||
        item.name === "Comprometido Obra"
    )
    .reduce((sum, item) => sum + item.value, 0);

  console.log("🔢 CÁLCULO PORCENTAJE:", {
    datosDisponibles: datosFinancieros.map((item) => ({
      nombre: item.name,
      valor: item.value,
    })),
    gastadoYComprometido,
    totalPresupuestado: totalPresupuestadoActualizado,
    porcentajeCalculado:
      totalPresupuestadoActualizado > 0
        ? (gastadoYComprometido / totalPresupuestadoActualizado) * 100
        : 0,
  });

  // ✅ CÁLCULO CORREGIDO: Porcentaje de ejecución total (gastado + comprometido)
  const porcentajeEjecucion =
    totalPresupuestadoActualizado > 0
      ? (gastadoYComprometido / totalPresupuestadoActualizado) * 100
      : 0;

  // ✅ DETECTAR SI HAY SOBREGASTO
  const itemSobregasto = datosFinancieros.find(
    (item) => item.name === "Sobregasto"
  );
  const haySobregasto = !!itemSobregasto;

  // ✅ TEXTO CENTRAL CORRECTO
  const textocentral = haySobregasto
    ? `${(porcentajeEjecucion - 100).toFixed(1)}%`
    : `${porcentajeEjecucion.toFixed(1)}%`;

  const subtextoCentral = haySobregasto ? "Sobregasto" : "Ejecutado + Compr.";
  const colorCentral = haySobregasto ? "#ef4444" : "#10b981";

  console.log("📊 DATOS GRÁFICO ACTUALIZADO:", {
    totalPresupuestadoActualizado,
    gastadoYComprometido,
    porcentajeEjecucion,
    haySobregasto,
    datosFinancieros,
  });

  return (
    <div style={{ height: "280px" }}>
      <div
        style={{
          height: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "12px",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "180px",
            height: "180px",
            position: "relative",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={datosFinancieros}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {datosFinancieros.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                position={{ x: 0, y: -10 }}
                formatter={(value, name) => {
                  // ✅ TOOLTIP CORREGIDO: Mostrar valor + porcentaje real
                  const porcentajeItem =
                    totalPresupuestadoActualizado > 0
                      ? ((value / totalPresupuestadoActualizado) * 100).toFixed(
                          1
                        )
                      : "0.0";

                  return [
                    `${formatCurrency(value)} (${porcentajeItem}% del total)`,
                    name,
                  ];
                }}
                contentStyle={{
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  padding: "8px 12px",
                  fontSize: "12px",
                  color: "#1e293b",
                }}
                labelStyle={{
                  color: "#1e293b",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* ✅ PORCENTAJE CENTRAL CORREGIDO */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "800",
                color: colorCentral,
                marginBottom: "2px",
              }}
            >
              {textocentral}
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#64748b",
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {subtextoCentral}
            </div>
          </div>
        </div>
      </div>

      {/* Total presupuestado */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "12px",
          padding: "12px",
          background: "#f8fafc",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            color: "#64748b",
            marginBottom: "4px",
            fontWeight: "500",
            letterSpacing: "0.5px",
          }}
        >
          PRESUPUESTADO{" "}
        </div>
        <div
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "#1e293b",
          }}
        >
          {formatCurrency(totalPresupuestadoActualizado)}
        </div>
      </div>

      {/* ✅ LEYENDA CORREGIDA - Porcentajes reales del total */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          justifyContent: "center",
        }}
      >
        {datosFinancieros.map((item, index) => {
          // ✅ PORCENTAJE REAL respecto al total presupuestado
          const porcentajeReal =
            totalPresupuestadoActualizado > 0
              ? ((item.value / totalPresupuestadoActualizado) * 100).toFixed(1)
              : "0.0";

          return (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 8px",
                background: "#f8fafc",
                borderRadius: "6px",
                border: `1px solid ${item.color}20`,
                fontSize: "10px",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "2px",
                  background: item.color,
                  flexShrink: 0,
                }}
              ></div>
              <span
                style={{
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                {item.name}
              </span>
              <span
                style={{
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                {porcentajeReal}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatMonthYear = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "short",
  }).format(date);
};

function Dashboard({ constructora, usuario, setUser }) {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función para navegar al flujo de caja
  const navegarAFlujoCaja = (moneda) => {
    // Determinar la ruta según el tipo de usuario
    const ruta = usuario?.tipo_usuario === 'cliente' ? '/cajas' : '/flujos-caja';
    
    console.log('🧭 Navegando a flujo de caja:', {
      tipoUsuario: usuario?.tipo_usuario,
      ruta,
      moneda,
      proyectoSeleccionado: proyectoSeleccionado?.id
    });
    
    // Si es constructora y hay proyecto seleccionado, pasar el proyecto como estado
    if (usuario?.tipo_usuario !== 'cliente' && proyectoSeleccionado) {
      navigate(ruta, { 
        state: { 
          proyectoSeleccionado: proyectoSeleccionado.id,
          monedaFiltro: moneda 
        } 
      });
    } else {
      // Para clientes o constructora sin proyecto, navegar normalmente
      navigate(ruta, { state: { monedaFiltro: moneda } });
    }
  };
  const [loadingData, setLoadingData] = useState(false);
  const [monedaVista, setMonedaVista] = useState("USD");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Evitar "flicker" de símbolo al cambiar moneda: usamos una moneda efectiva
  // para formatear, que se actualiza solo cuando terminan los recálculos.
  const [monedaCalculos, setMonedaCalculos] = useState("USD");
  const [recalculandoMoneda, setRecalculandoMoneda] = useState(false);
  // Estados para permisos de cliente (deben declararse antes de efectos que los usen)
  const [datosCliente, setDatosCliente] = useState(null);
  const [verificandoPermisos, setVerificandoPermisos] = useState(true);

  // Recalcular métricas monetarias al cambiar la moneda de vista
  useEffect(() => {
    if (constructora?.id && proyectoSeleccionado && !verificandoPermisos) {
      (async () => {
        setRecalculandoMoneda(true);
        try {
        await Promise.all([
          cargarGastoTotal(),
          cargarDistribucionGastos(),
          cargarGastosMensuales(),
          (async () => {
            console.log("🔍 DEBUG - Llamando cargarDatosAdicionales desde useEffect monedaVista");
            await cargarDatosAdicionales();
          })(), // ✅ NUEVO: Recalcular adicionales al cambiar moneda
        ]);
          
          // Cargar indicadores fiscales
          const indicadores = await calcularIndicadoresFiscales();
          setIndicadoresFiscales(indicadores);
          
          setMonedaCalculos(monedaVista);
        } finally {
          setRecalculandoMoneda(false);
        }
      })();
    }
  }, [monedaVista, constructora, proyectoSeleccionado, datosCliente]);

  // Estados para los datos del dashboard
  const [infoObra, setInfoObra] = useState(null);
  const [dineroCajas, setDineroCajas] = useState({ ARS: 0, USD: 0 });
  const [gastoPorMetro, setGastoPorMetro] = useState(0);
  const [distribucionGastos, setDistribucionGastos] = useState([]);
  const [gastosMensuales, setGastosMensuales] = useState([]);
  const [gastoTotal, setGastoTotal] = useState(0);

  // ✅ NUEVO: Estado para datos de adicionales
  const [datosAdicionales, setDatosAdicionales] = useState(null);

  // ✅ NUEVO: Estados para fecha de finalización desde cronograma
  const [fechaFinalizacionCronograma, setFechaFinalizacionCronograma] =
    useState(null);
  const [duracionTotalProyecto, setDuracionTotalProyecto] = useState(null);

  // ✅ NUEVO: Estados para el estado financiero
  const [datosFinancieros, setDatosFinancieros] = useState([]);
  const [totalPresupuestadoActualizado, setTotalPresupuestadoActualizado] =
    useState(0);
  const [totalPresupuestadoInicial, setTotalPresupuestadoInicial] = useState(0);
  const [cotizacionDolar, setCotizacionDolar] = useState(null);
  const [estadoFinancieroListo, setEstadoFinancieroListo] = useState(false);

  // ✅ NUEVO: Estados para Honorarios y Mano de Obra
  const [datosHonorarios, setDatosHonorarios] = useState(null);
  const [datosManoObra, setDatosManoObra] = useState(null);
  const [datosLlaveEnMano, setDatosLlaveEnMano] = useState(null);
  const [indicesCAC, setIndicesCAC] = useState([]);

  // ✅ NUEVO: Estado para presupuestos iniciales
  const [presupuestosIniciales, setPresupuestosIniciales] = useState([]);

  const colorVista = getVistaColor();

  // ✅ NUEVO: Control de carga completa para evitar renders intermedios
  const [datosCompletamenteCargados, setDatosCompletamenteCargados] =
    useState(false);

  // 🎉 NUEVO: Estado para mostrar página de éxito de suscripción
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);

  // ✅ NUEVO: Estado para indicadores fiscales
  const [indicadoresFiscales, setIndicadoresFiscales] = useState({ ivaDeducible: 0, ahorroEstimado: 0 });

  // ✅ FUNCIÓN PARA CALCULAR PRESUPUESTOS POR METRO
  const calcularPresupuestosPorMetro = () => {
    if (
      !proyectoSeleccionado?.metros_computo ||
      proyectoSeleccionado.metros_computo <= 0
    ) {
      return {
        presupuestoActualizadoPorMetro: 0,
      };
    }

    const metros = parseFloat(proyectoSeleccionado.metros_computo);

    // ✅ CALCULAR PRESUPUESTO INICIAL POR METRO
    const presupuestoInicialPorMetro = totalPresupuestadoInicial / metros;
    const presupuestoActualizadoPorMetro =
      totalPresupuestadoActualizado / metros;

    return {
      presupuestoInicialPorMetro,
      presupuestoActualizadoPorMetro,
    };
  };

  // ✅ FUNCIÓN PARA CALCULAR INDICADORES FISCALES
  const calcularIndicadoresFiscales = async () => {
    if (!proyectoSeleccionado?.id) return { ivaDeducible: 0, ahorroEstimado: 0 };

    try {
      // Obtener todas las salidas con factura del proyecto
      const { data: salidasConFactura, error } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, tipo_factura, tiene_factura")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .eq("tiene_factura", true);

      if (error || !salidasConFactura) {
        return { ivaDeducible: 0, ahorroEstimado: 0 };
      }

      const toARS = (salida) => {
        const monto = parseFloat(salida.monto || 0);
        const tipoCambio = parseFloat(salida.tipo_cambio || 1);
        if (salida.moneda === 'USD') {
          const tc = Number.isFinite(tipoCambio) && tipoCambio > 0 ? tipoCambio : 1;
          return monto * tc;
        }
        return monto;
      };

      const facturasA = salidasConFactura.filter(s => s.tipo_factura === 'A');
      const facturasBC = salidasConFactura.filter(s => s.tipo_factura === 'B' || s.tipo_factura === 'C');

      const montoA_ARS = facturasA.reduce((sum, s) => sum + toARS(s), 0);
      const montoBC_ARS = facturasBC.reduce((sum, s) => sum + toARS(s), 0);

      const ivaDeducible = montoA_ARS * 0.21; // 21% de facturas A
      const gastoDeducibleGanancias = (montoA_ARS / 1.21) + montoBC_ARS; // A neto + B/C total
      const ahorroEstimadoGanancias = gastoDeducibleGanancias * 0.30; // 30%

      return {
        ivaDeducible,
        ahorroEstimado: ahorroEstimadoGanancias,
      };
    } catch (error) {
      console.error('Error calculando indicadores fiscales:', error);
      return { ivaDeducible: 0, ahorroEstimado: 0 };
    }
  };

  // ✅ NUEVO: useEffect para controlar cuando todos los datos están listos
  useEffect(() => {
    if (!proyectoSeleccionado || loadingData) {
      setDatosCompletamenteCargados(false);
      return;
    }

    // Verificar que todos los datos necesarios estén cargados
    const tieneLlaveEnMano =
      datosLlaveEnMano &&
      !datosLlaveEnMano.error &&
      datosLlaveEnMano.total_pagado !== undefined;
    const tieneHonorarios =
      datosHonorarios &&
      !datosHonorarios.error &&
      datosHonorarios.total_pagado !== undefined;
    const tieneManoObra =
      datosManoObra &&
      !datosManoObra.error &&
      datosManoObra.total_pagado !== undefined;

    // Para llave en mano: solo necesita datosLlaveEnMano
    if (proyectoSeleccionado.tipo_obra === "llave_en_mano") {
      if (tieneLlaveEnMano) {
        console.log("✅ DATOS LLAVE EN MANO COMPLETAMENTE CARGADOS");
        setDatosCompletamenteCargados(true);
      } else {
        console.log("⏳ ESPERANDO DATOS LLAVE EN MANO...");
        setDatosCompletamenteCargados(false);
      }
    } else {
      // Para administración: necesita honorarios Y mano de obra (si está separada)
      const datosNecesarios =
        tieneHonorarios &&
        (proyectoSeleccionado.mano_obra_separada ? tieneManoObra : true);

      if (datosNecesarios) {
        console.log("✅ DATOS ADMINISTRACIÓN COMPLETAMENTE CARGADOS");
        setDatosCompletamenteCargados(true);
      } else {
        console.log("⏳ ESPERANDO DATOS ADMINISTRACIÓN...", {
          tieneHonorarios,
          tieneManoObra,
          manoObraSeparada: proyectoSeleccionado.mano_obra_separada,
        });
        setDatosCompletamenteCargados(false);
      }
    }
  }, [
    datosHonorarios,
    datosManoObra,
    datosLlaveEnMano,
    proyectoSeleccionado,
    loadingData,
  ]);

  // ✅ CONSOLIDADO: Ejecutar estado financiero y totales cuando TODOS los datos estén listos
  useEffect(() => {
    if (!proyectoSeleccionado || loadingData || !datosCompletamenteCargados) {
      return;
    }

    console.log(
      "🚀 EJECUTANDO ESTADO FINANCIERO Y TOTALES CON DATOS COMPLETAMENTE CARGADOS"
    );
    
    // Ejecutar ambos procesos de forma secuencial para evitar bucles
    const ejecutarCalculos = async () => {
      try {
        // 1. Primero obtener totales
        const { totalInicial, totalActualizado } =
          await obtenerTotalesDesdeTablas();
        setTotalPresupuestadoActualizado(totalActualizado);
        setTotalPresupuestadoInicial(totalInicial);
        
        // 2. Luego cargar estado financiero
        await cargarEstadoFinanciero();
      } catch (error) {
        console.error("❌ Error en cálculos:", error);
      }
    };

    ejecutarCalculos();
  }, [datosCompletamenteCargados, monedaVista, cotizacionDolar]); // ✅ INCLUIR monedaVista y cotizacionDolar para recalcular cuando cambien

  // ✅ REMOVIDO: Este useEffect era redundante y causaba bucles infinitos
  // El recálculo cuando cambia monedaVista ya está manejado en el useEffect consolidado arriba

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        // Dashboard no tiene modales, pero mantenemos la consistencia
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Verificar permisos de cliente al inicio
  useEffect(() => {
    const verificarPermisos = async () => {
      if (!usuario || !constructora) {
        setVerificandoPermisos(false);
        return;
      }

      console.log("🔍 VERIFICANDO PERMISOS PARA USUARIO:", usuario);

      const permisos = await obtenerPermisosCliente(usuario, constructora);
      console.log("✅ PERMISOS OBTENIDOS:", permisos);

      setDatosCliente(permisos);

      // Verificar si puede acceder a esta vista
      if (
        permisos.esCliente &&
        !puedeAccederAVista(permisos.permisos, "dashboard")
      ) {
        console.log("❌ Cliente no puede acceder al dashboard");
        setVerificandoPermisos(false);
        return;
      }

      setVerificandoPermisos(false);
    };

    verificarPermisos();
  }, [usuario, constructora]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id && !verificandoPermisos) {
      cargarProyectos();
      obtenerCotizacionDolar();
      cargarIndicesCAC(); // ✅ AGREGAR ESTA LÍNEA
    }
  }, [constructora, verificandoPermisos, datosCliente]);

  // Effect para limpiar estados inmediatamente al cambiar proyecto
  useEffect(() => {
    if (proyectoSeleccionado) {
      // Limpiar estados inmediatamente ANTES de cargar nuevos datos
      setDatosHonorarios(null);
      setDatosManoObra(null);
      setDatosLlaveEnMano(null);
      setDatosFinancieros([]);
      setEstadoFinancieroListo(false); // ✅ RESETEAR ESTADO

      console.log(
        "🔄 PROYECTO CAMBIADO - Estados limpiados:",
        proyectoSeleccionado.codigo
      );
    } else {
      // ✅ LIMPIAR DATOS CUANDO NO HAY PROYECTO SELECCIONADO
      setDatosHonorarios(null);
      setDatosManoObra(null);
      setDatosLlaveEnMano(null);
      setDatosFinancieros([]);
      setEstadoFinancieroListo(false);
      
      console.log("🧹 NO HAY PROYECTO - Estados limpiados");
    }
  }, [proyectoSeleccionado?.id]); // ← Solo cuando cambia el ID del proyecto

  useEffect(() => {
    if (proyectoSeleccionado && indicesCAC.length > 0) {
      cargarDatosProyecto();
    }
  }, [proyectoSeleccionado, indicesCAC]); // ✅ REMOVIDO monedaVista

  // 🔔 DETECTAR PAGO EXITOSO
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment");

    if (paymentStatus === "success") {
      console.log("🎉 PAGO EXITOSO DETECTADO - Activando suscripción...");

      // Activar suscripción del usuario
      activateUserSubscription();

      // Limpiar URL para evitar reactivaciones
      window.history.replaceState({}, document.title, window.location.pathname);

      // Mostrar página de éxito de suscripción
      setShowSubscriptionSuccess(true);
    }
  }, []); // Solo ejecutar una vez al montar

  // 🎯 FUNCIÓN: Activar suscripción automática tras pago exitoso
  const activateUserSubscription = async () => {
    try {
      console.log(
        "🔄 Activando suscripción AUTOMÁTICA para usuario:",
        usuario?.email_empleado
      );

      if (!usuario?.id) {
        console.error("❌ No hay usuario logueado");
        return;
      }

      const now = new Date();
      const nextPaymentDate = new Date(now);
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);

      // Actualizar usuario en Supabase con suscripción automática activa
      const { error } = await supabase
        .from("usuarios")
        .update({
          subscription_status: "active",
          subscription_plan: "monthly_subscription_auto", // Indica que es automática
          subscription_start_date: now.toISOString(),
          next_payment_date: nextPaymentDate.toISOString(),
          subscription_type: "mercadopago_automatic", // Nuevo campo
          updated_at: now.toISOString(),
        })
        .eq("id", usuario.id);

      if (error) {
        console.error("❌ Error activando suscripción automática:", error);
        return;
      }

      console.log(
        "✅ Suscripción AUTOMÁTICA activada exitosamente - Mercado Pago cobrará cada mes"
      );

      // Actualizar el estado del usuario en la app
      setUser((prevUser) => ({
        ...prevUser,
        subscription_status: "active",
        subscription_plan: "monthly_subscription_auto",
        subscription_start_date: now.toISOString(),
        next_payment_date: nextPaymentDate.toISOString(),
        subscription_type: "mercadopago_automatic",
      }));
    } catch (error) {
      console.error("❌ Error en activateUserSubscription:", error);
    }
  };

  // 🎯 FUNCIÓN: Continuar al dashboard después del pago exitoso
  const handleContinueToOnboarding = () => {
    setShowSubscriptionSuccess(false);
    console.log("🚀 Continuando al dashboard con suscripción activa...");
    // El usuario ya puede usar la app normalmente
  };

  // ✅ NUEVO: Cargar cotización del dólar oficial
  const obtenerCotizacionDolar = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setCotizacionDolar(data.venta);
      console.log("💰 Dólar oficial cargado:", data.venta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setCotizacionDolar(1000); // Fallback
    }
  };

  const cargarIndicesCAC = async () => {
    try {
      const { data, error } = await supabase
        .from("indices_cac")
        .select("*")
        .order("fecha", { ascending: true });

      if (!error && data) {
        setIndicesCAC(data);
        console.log("🗄️ ÍNDICES CAC CARGADOS:", data.length, "registros");
      }
    } catch (err) {
      console.error("Error cargando índices CAC:", err);
    }
  };

  const actualizarPresupuestoConCAC = async (presupuesto, indicesCAC) => {
    // Si no está en ARS o no tiene fecha de creación, no actualizar
    if (presupuesto.moneda !== "ARS" || !presupuesto.created_at) {
      return {
        ...presupuesto,
        monto_actualizado: null,
        factor_actualizacion: null,
        observacion_actualizacion:
          "No aplica actualización (no es ARS o sin fecha)",
      };
    }

    try {
      // Obtener mes de creación del presupuesto
      const fechaCreacion = new Date(presupuesto.created_at);
      const mesCreacion = fechaCreacion.toISOString().substring(0, 7) + "-01";

      // Obtener último mes disponible de índices
      const indicesOrdenados = [...indicesCAC].sort(
        (a, b) => new Date(b.fecha) - new Date(a.fecha)
      );
      const ultimoIndice = indicesOrdenados[0];

      if (!ultimoIndice) {
        return {
          ...presupuesto,
          monto_actualizado: null,
          factor_actualizacion: null,
          observacion_actualizacion: "Sin índices CAC disponibles",
        };
      }

      // Buscar índice del mes de creación
      const indiceCreacion = indicesCAC.find((i) => i.fecha === mesCreacion);

      if (!indiceCreacion) {
        console.log(
          `⚠️ No hay índice CAC para mes de creación: ${mesCreacion}`
        );
        return {
          ...presupuesto,
          monto_actualizado: null,
          factor_actualizacion: null,
          observacion_actualizacion: `Sin índice para mes de creación: ${mesCreacion}`,
        };
      }

      // Calcular factor de actualización usando CAC General
      const cacCreacion = parseFloat(indiceCreacion.cac_general);
      const cacActual = parseFloat(ultimoIndice.cac_general);

      if (!cacCreacion || !cacActual || cacCreacion <= 0) {
        console.log(`⚠️ Índices CAC inválidos:`, { cacCreacion, cacActual });
        return {
          ...presupuesto,
          monto_actualizado: null,
          factor_actualizacion: null,
          observacion_actualizacion: "Índices CAC inválidos",
        };
      }

      const factorActualizacion = cacActual / cacCreacion;
      const montoActualizado =
        parseFloat(presupuesto.monto_presupuestado) * factorActualizacion;

      return {
        ...presupuesto,
        monto_actualizado: montoActualizado,
        factor_actualizacion: factorActualizacion,
        observacion_actualizacion: `Actualizado con CAC (${cacCreacion.toFixed(
          2
        )} → ${cacActual.toFixed(2)})`,
      };
    } catch (error) {
      console.error("❌ Error actualizando presupuesto con CAC:", error);
      return {
        ...presupuesto,
        monto_actualizado: null,
        factor_actualizacion: null,
        observacion_actualizacion: `Error en actualización: ${error.message}`,
      };
    }
  };

  const cargarProyectos = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("proyectos")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null);

      // Aplicar filtro si es cliente
      if (datosCliente?.esCliente && datosCliente?.proyectoId) {
        console.log(
          "🔒 APLICANDO FILTRO DE CLIENTE - Solo proyecto ID:",
          datosCliente.proyectoId
        );
        query = query.eq("id", datosCliente.proyectoId);
      } else {
        console.log("👤 USUARIO EMPLEADO - Cargando todos los proyectos");
        query = query
          .order("obra_finalizada", { ascending: true })
          .order("codigo");
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error cargando proyectos:", error);
        setProyectos([]);
      } else {
        console.log("📊 PROYECTOS CARGADOS:", data?.length || 0);
        setProyectos(data || []);

        if (data?.length > 0) {
          // Si es cliente: Seleccionar automáticamente su proyecto
          if (datosCliente?.esCliente) {
            const proyectoCliente = data.find(
              (p) => p.id === datosCliente.proyectoId
            );
            if (proyectoCliente) {
              console.log(
                "🎯 SELECCIONANDO PROYECTO DEL CLIENTE:",
                proyectoCliente.codigo
              );
              setProyectoSeleccionado(proyectoCliente);
            }
          } else {
            // Para empleados: intentar cargar la última selección del localStorage
            const ultimoProyectoId = localStorage.getItem(
              `dashboard_proyecto_${constructora.id}`
            );
            let proyectoSeleccionar = null;

            if (ultimoProyectoId) {
              // Buscar si el proyecto guardado aún existe y está activo
              proyectoSeleccionar = data.find(
                (p) => p.id === ultimoProyectoId && !p.obra_finalizada
              );
              if (proyectoSeleccionar) {
                console.log(
                  "🎯 CARGANDO ÚLTIMA SELECCIÓN:",
                  proyectoSeleccionar.codigo
                );
              }
            }

            // Si no hay última selección válida, usar el primer activo o el primero
            if (!proyectoSeleccionar) {
              proyectoSeleccionar =
                data.find((p) => !p.obra_finalizada) || data[0];
              console.log(
                "🎯 SELECCIONANDO PROYECTO POR DEFECTO:",
                proyectoSeleccionar.codigo
              );
            }

            setProyectoSeleccionado(proyectoSeleccionar);
          }
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setProyectos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosProyecto = async () => {
    if (!proyectoSeleccionado) return;

    setLoadingData(true);
    try {
      await Promise.all([
        cargarInfoObra(),
        cargarDineroCajas(),
        cargarGastoTotal(),
        cargarDistribucionGastos(),
        cargarGastosMensuales(),
        cargarFechaFinalizacionCronograma(),
        (async () => {
          console.log("🔍 DEBUG - Llamando cargarDatosAdicionales desde cargarDatosProyecto");
          await cargarDatosAdicionales();
        })(), // ✅ NUEVO: Cargar datos de adicionales
      ]);

      // ✅ CARGAR HONORARIOS/MO PRIMERO
      await cargarHonorariosManoObra();

      // ✅ OBTENER TOTALES DESDE LAS TABLAS UNA SOLA VEZ
      const { totalInicial, totalActualizado } =
        await obtenerTotalesDesdeTablas();

      // ✅ GUARDAR LOS TOTALES EN ESTADO
      setTotalPresupuestadoActualizado(totalActualizado);
      setTotalPresupuestadoInicial(totalInicial);

      // ✅ LUEGO CARGAR ESTADO FINANCIERO (que ya no necesita consultar las tablas)
      await cargarEstadoFinanciero();
    } catch (err) {
      console.error("Error cargando datos del proyecto:", err);
    }
    setLoadingData(false);
  };

  // ✅ NUEVA FUNCIÓN: Cargar datos de Honorarios y Mano de Obra CON ACTUALIZACIONES
  const cargarHonorariosManoObra = async () => {
    const proyecto = proyectoSeleccionado;

    try {
      if (proyecto.tipo_obra === "llave_en_mano") {
        // Para llave en mano, cargar con actualizaciones
        await cargarDatosLlaveEnManoConActualizacion();
      } else {
        // Para administración, cargar honorarios y mano de obra con actualizaciones
        await Promise.all([
          cargarDatosHonorariosConActualizacion(),
          // ✅ SOLO CARGAR MANO DE OBRA SI ESTÁ SEPARADA
          proyecto.mano_obra_separada && proyecto.monto_mano_obra
            ? cargarDatosManoObraConActualizacion()
            : Promise.resolve(),
        ]);
      }
    } catch (err) {
      console.error("Error cargando datos de honorarios y mano de obra:", err);
    }
  };

  // FUNCIONES HELPER COPIADAS EXACTAMENTE DE PRESUPUESTOS INICIALES

  const cargarDatosLlaveEnManoConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.monto_total_llave_en_mano) {
      setDatosLlaveEnMano(null);
      return;
    }

    console.log("🏗️ CARGANDO LLAVE EN MANO CON ACTUALIZACIONES...");

    // Obtener todos los pagos del proyecto
    const { data: pagos, error: errorPagos } = await supabase
      .from("entradas")
      .select(
        "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
      )
      .eq("proyecto_id", proyecto.id)
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    if (errorPagos) {
      console.error("Error cargando pagos llave en mano:", errorPagos);
      setDatosLlaveEnMano({ error: "Error cargando pagos" });
      return;
    }

    // Calcular con sistema de actualización EXACTO como Presupuestos Iniciales
    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_total_llave_en_mano,
      proyecto.moneda_llave_en_mano,
      proyecto.metodo_actualizacion,
      proyecto.fecha_inicio_actualizacion_llave_en_mano,
      pagos || [],
      "llave_en_mano",
      !!proyecto.metodo_actualizacion
    );

    const totalActualizado =
      resultado.resumenFinal?.montoActualizado ||
      proyecto.monto_total_llave_en_mano;

    setDatosLlaveEnMano({
      monedaInicial: proyecto.moneda_llave_en_mano,
      resumenFinal: {
        pagosAcumulados: resultado.resumenFinal?.pagosAcumulados || 0,
        saldoPendiente: resultado.resumenFinal?.saldoPendiente || 0,
        montoActualizado: totalActualizado,
      },
      // ✅ AGREGAR CAMPOS PARA COMPATIBILIDAD CON ESTADO FINANCIERO
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      montoInicial: proyecto.monto_total_llave_en_mano, // ← AGREGAR MONTO INICIAL
    });
    setDatosManoObra(null); // No hay mano de obra separada en llave en mano

    console.log("✅ LLAVE EN MANO CARGADA:", {
      montoInicial: proyecto.monto_total_llave_en_mano,
      totalActualizado: totalActualizado,
      pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo: resultado.resumenFinal?.saldoPendiente || 0,
    });
  };

  const cargarDatosHonorariosConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (proyecto.tipo_obra === "administracion_porcentaje") {
      // Unidades por evento: misma lógica que HonorariosMO
      let porcentaje = 0;
      let rawPorcentaje = proyecto.porcentaje_honorario;
      if (typeof rawPorcentaje === "string") rawPorcentaje = rawPorcentaje.replace(/,/g, ".");
      porcentaje = parseFloat(rawPorcentaje) || 0;

      const seActualiza = proyecto.honorarios_se_actualizan;
      const metodoActualizacion = proyecto.metodo_actualizacion_honorarios;
      const monedaHonorarios = proyecto.moneda_honorarios;

      let unidadesTotalesCalculadas = 0;
      const gastosConUnidades = [];
      let totalGastado = 0;
      let totalEntradasMO = 0;

      // Salidas (INCLUIR TODAS: normales + adicionales, porque honorarios % aplican sobre todo)
      const { data: salidas } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, fecha, es_adicional")
        .eq("proyecto_id", proyecto.id)
        .eq('activo', true)
        .is('deleted_at', null);
      if (seActualiza && metodoActualizacion && salidas && salidas.length > 0) {
        for (const salida of salidas) {
          const montoEnMonedaHonorarios = convertirMonto(
            salida.monto,
            salida.moneda,
            monedaHonorarios,
            salida.tipo_cambio
          );
          const honorarioSalida = montoEnMonedaHonorarios * (porcentaje / 100);
          const indiceSalida = await obtenerIndicePorFecha(
            formatearFechaParaIndice(new Date(formatearFechaSinTimezone(salida.fecha) + "T00:00:00")),
            metodoActualizacion,
            "honorarios"
          );
          const unidadesSalida = indiceSalida ? honorarioSalida / indiceSalida : 0;
          unidadesTotalesCalculadas += unidadesSalida;
          gastosConUnidades.push({
            ...salida,
            tipo: "salida",
            montoOriginal: salida.monto,
            montoConvertido: montoEnMonedaHonorarios,
            porcentajeAplicado: porcentaje,
            honorarioCalculado: honorarioSalida,
            indiceUsado: indiceSalida,
            unidades: unidadesSalida,
          });
          totalGastado += montoEnMonedaHonorarios;
        }
      } else if (salidas) {
        salidas.forEach((s) => {
          totalGastado += convertirMonto(s.monto, s.moneda, monedaHonorarios, s.tipo_cambio);
        });
      }

      // Entradas Mano de Obra
      const { data: cajasManoObra } = await supabase
        .from("cajas")
        .select("id")
        .eq("proyecto_id", proyecto.id)
        .eq("tipo_caja", "mano_obra");
      const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];
      if (idsCajasManoObra.length > 0) {
        const { data: entradasMO } = await supabase
          .from("entradas")
          .select("monto, moneda, tipo_cambio, caja_destino_id, fecha")
          .eq("proyecto_id", proyecto.id)
          .in("caja_destino_id", idsCajasManoObra)
          .eq('activo', true)
          .is('deleted_at', null);
        if (seActualiza && metodoActualizacion && entradasMO) {
          for (const entrada of entradasMO) {
            const montoEnMonedaHonorarios = convertirMonto(
              entrada.monto,
              entrada.moneda,
              monedaHonorarios,
              entrada.tipo_cambio
            );
            const honorarioEntrada = montoEnMonedaHonorarios * (porcentaje / 100);
            const indiceEntrada = await obtenerIndicePorFecha(
              formatearFechaParaIndice(new Date(formatearFechaSinTimezone(entrada.fecha) + "T00:00:00")),
              metodoActualizacion,
              "honorarios"
            );
            const unidadesEntrada = indiceEntrada ? honorarioEntrada / indiceEntrada : 0;
            unidadesTotalesCalculadas += unidadesEntrada;
            gastosConUnidades.push({
              ...entrada,
              tipo: "entrada_mo",
              montoOriginal: entrada.monto,
              montoConvertido: montoEnMonedaHonorarios,
              porcentajeAplicado: porcentaje,
              honorarioCalculado: honorarioEntrada,
              indiceUsado: indiceEntrada,
              unidades: unidadesEntrada,
            });
            totalEntradasMO += montoEnMonedaHonorarios;
          }
        } else if (entradasMO) {
          entradasMO.forEach((e) => {
            totalEntradasMO += convertirMonto(e.monto, e.moneda, monedaHonorarios, e.tipo_cambio);
          });
        }
      }

      // Pagos de honorarios
      const { data: pagosHonorarios, error: errorPagos } = await supabase
        .from("entradas")
        .select(
          `fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)`
        )
        .eq("proyecto_id", proyecto.id)
        .ilike("conceptos_entrada.nombre", "%honorario%")
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });
      if (errorPagos) {
        setDatosHonorarios({ error: "Error cargando pagos" });
        return;
      }

      const resultado = seActualiza && metodoActualizacion
        ? await calcularConSistemaActualizacionPorcentajeAdicionalesShared(
            unidadesTotalesCalculadas,
            gastosConUnidades,
            monedaHonorarios,
            metodoActualizacion,
            pagosHonorarios || [],
            "honorarios",
            obtenerIndicePorFecha,
            obtenerUltimoIndiceRealDisponible,
            supabase,
            proyecto.id
          )
        : {
            resumenFinal: {
              pagosAcumulados: (pagosHonorarios || []).reduce(
                (sum, p) => sum + convertirMonto(p.monto, p.moneda, monedaHonorarios, p.tipo_cambio),
                0
              ),
              saldoPendiente: ((totalGastado + totalEntradasMO) * (porcentaje / 100)) - (pagosHonorarios || []).reduce(
                (sum, p) => sum + convertirMonto(p.monto, p.moneda, monedaHonorarios, p.tipo_cambio),
                0
              ),
              montoActualizado: (totalGastado + totalEntradasMO) * (porcentaje / 100),
            },
          };

      setDatosHonorarios({
        monedaInicial: monedaHonorarios,
        resumenFinal: {
          pagosAcumulados: resultado.resumenFinal?.pagosAcumulados || 0,
          saldoPendiente: resultado.resumenFinal?.saldoPendiente || 0,
          montoActualizado: resultado.resumenFinal?.montoActualizado || 0,
        },
        total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
        saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
        montoInicial: (totalGastado + totalEntradasMO) * (porcentaje / 100),
      });
      return;
    }

    if (!proyecto.honorarios) {
      setDatosHonorarios({ error: "No hay honorarios configurados" });
      return;
    }

    console.log("👨‍💼 CARGANDO HONORARIOS CON ACTUALIZACIONES...");

    // Obtener pagos de honorarios (MISMA LÓGICA EXISTENTE)
    const { data: pagosHonorarios1, error: error1 } = await supabase
      .from("entradas")
      .select(
        `
    fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante,
    conceptos_entrada!inner(nombre)
  `
      )
      .eq("proyecto_id", proyecto.id)
      .ilike("conceptos_entrada.nombre", "%honorario%")
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    let pagosHonorarios = pagosHonorarios1 || [];

    // Método 2 si no encuentra nada
    if ((!pagosHonorarios || pagosHonorarios.length === 0) && !error1) {
      const { data: todosPagos, error: error2 } = await supabase
        .from("entradas")
        .select(
          `
      fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante,
      concepto, conceptos_entrada(nombre)
    `
        )
        .eq("proyecto_id", proyecto.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (!error2 && todosPagos) {
        pagosHonorarios = todosPagos.filter((pago) => {
          const nombreConcepto =
            pago.conceptos_entrada?.nombre || pago.concepto || "";
          return nombreConcepto.toLowerCase().includes("honorario");
        });
      }
    }

    if (error1) {
      console.error("Error cargando pagos honorarios:", error1);
      setDatosHonorarios({ error: "Error cargando pagos" });
      return;
    }

    // Calcular con sistema de actualización EXACTO como Presupuestos Iniciales
    const resultado = await calcularConSistemaActualizacion(
      proyecto.honorarios,
      proyecto.moneda_honorarios,
      proyecto.metodo_actualizacion_honorarios,
      proyecto.fecha_inicio_actualizacion_honorarios,
      pagosHonorarios || [],
      "honorarios",
      proyecto.honorarios_se_actualizan
    );

    const totalActualizado =
      resultado.resumenFinal?.montoActualizado || proyecto.honorarios;

    console.log("🔍 DEBUG - Moneda honorarios:", proyecto.moneda_honorarios);

    setDatosHonorarios({
      monedaInicial: proyecto.moneda_honorarios,
      resumenFinal: {
        pagosAcumulados: resultado.resumenFinal?.pagosAcumulados || 0,
        saldoPendiente: resultado.resumenFinal?.saldoPendiente || 0,
        montoActualizado: totalActualizado,
      },
      // ✅ AGREGAR CAMPOS PARA COMPATIBILIDAD CON ESTADO FINANCIERO
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      montoInicial: proyecto.honorarios, // ← AGREGAR MONTO INICIAL
    });

    console.log("✅ HONORARIOS CARGADOS:", {
      montoInicial: proyecto.honorarios,
      totalActualizado: totalActualizado,
      pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo: resultado.resumenFinal?.saldoPendiente || 0,
    });
  };

  const cargarDatosManoObraConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.mano_obra_separada || !proyecto.monto_mano_obra) {
      console.log(
        "🚫 Proyecto sin mano de obra separada o sin monto configurado"
      );
      setDatosManoObra(null);
      return;
    }

    console.log("👷‍♂️ CARGANDO MANO DE OBRA CON ACTUALIZACIONES...");

    // Obtener cajas de mano de obra del proyecto (MISMA LÓGICA EXISTENTE)
    const { data: cajasManoObra } = await supabase
      .from("cajas")
      .select("id")
      .eq("proyecto_id", proyecto.id)
      .eq("tipo_caja", "mano_obra");

    const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];

    let pagos = [];
    if (idsCajasManoObra.length > 0) {
      const { data: pagosMO, error: errorPagos } = await supabase
        .from("entradas")
        .select(
          "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
        )
        .eq("proyecto_id", proyecto.id)
        .in("caja_destino_id", idsCajasManoObra)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (!errorPagos) {
        pagos = pagosMO || [];
      }
    }

    // Calcular con sistema de actualización EXACTO como Presupuestos Iniciales
    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_mano_obra,
      proyecto.moneda_mano_obra,
      proyecto.metodo_actualizacion_mano_obra,
      proyecto.fecha_inicio_actualizacion_mano_obra,
      pagos,
      "mano_obra",
      proyecto.mano_obra_se_actualiza
    );

    const totalActualizado =
      resultado.resumenFinal?.montoActualizado || proyecto.monto_mano_obra;

    console.log("🔍 DEBUG - Moneda mano de obra:", proyecto.moneda_mano_obra);

    setDatosManoObra({
      monedaInicial: proyecto.moneda_mano_obra,
      resumenFinal: {
        pagosAcumulados: resultado.resumenFinal?.pagosAcumulados || 0,
        saldoPendiente: resultado.resumenFinal?.saldoPendiente || 0,
        montoActualizado: totalActualizado,
      },
      // ✅ AGREGAR CAMPOS PARA COMPATIBILIDAD CON ESTADO FINANCIERO
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      montoInicial: proyecto.monto_mano_obra, // ← AGREGAR MONTO INICIAL
    });

    console.log("✅ MANO DE OBRA CARGADA:", {
      montoInicial: proyecto.monto_mano_obra,
      totalActualizado: totalActualizado,
      pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo: resultado.resumenFinal?.saldoPendiente || 0,
    });
  };

  // FUNCIÓN: Formatear fecha sin conversión de timezone
  const formatearFechaSinTimezone = (fechaString) => {
    if (!fechaString) return new Date().toISOString().split("T")[0];
    if (fechaString.includes("T")) {
      return fechaString.split("T")[0];
    }
    return fechaString;
  };

  // FUNCIÓN: Detectar si es método personalizado
  const esMetodoPersonalizado = (metodo) => {
    if (!metodo) return false;
    return !["CAC_GENERAL", "CAC_MATERIALES", "CAC_MANO_OBRA"].includes(metodo);
  };

  // =============================================
  // ESPECIAL: Porcentaje + Índice por evento (unidades)
  // Usar util compartido para unificar lógica
  // =============================================
  const calcularConSistemaActualizacionPorcentajeAdicionales =
    calcularConSistemaActualizacionPorcentajeAdicionalesShared;

  // FUNCIÓN PRINCIPAL: CALCULAR CON SISTEMA DE ACTUALIZACIÓN
  const calcularConSistemaActualizacion = async (
    montoInicial,
    monedaInicial,
    metodoActualizacion,
    fechaInicio,
    pagos,
    tipo,
    seActualiza
  ) => {
    console.log(`🔄 CALCULANDO CON SISTEMA:`, {
      montoInicial,
      seActualiza,
      metodoActualizacion,
      tipo,
      esPersonalizado: esMetodoPersonalizado(metodoActualizacion),
    });

    // SI NO SE ACTUALIZA: Cálculo simple sin índices
    if (!seActualiza || !metodoActualizacion || !fechaInicio) {
      console.log(`❌ SIN ACTUALIZACIÓN - Cálculo directo`);

      const totalPagadoConvertido = pagos.reduce((sum, pago) => {
        return (
          sum +
          convertirMonto(
            pago.monto,
            pago.moneda,
            monedaInicial,
            pago.tipo_cambio
          )
        );
      }, 0);

      return {
        evolucion: [
          {
            mes: "Presupuesto",
            fecha: new Date().toISOString().split("T")[0],
            montoInicial: parseFloat(montoInicial),
            totalPagado: totalPagadoConvertido,
            saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
            observaciones: "Sin actualización configurada",
          },
        ],
        resumenFinal: {
          montoActualizado: parseFloat(montoInicial),
          pagosAcumulados: totalPagadoConvertido,
          saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
        },
      };
    }

    // CON ACTUALIZACIÓN: Lógica de unidades
    console.log(`✅ CON ACTUALIZACIÓN - Sistema: ${metodoActualizacion}`);

    // CORREGIR FECHA SIN TIMEZONE
    const fechaInicioSinTZ = formatearFechaSinTimezone(fechaInicio);
    const fechaInicioDate = new Date(fechaInicioSinTZ + "T00:00:00");
    const mesInicio = formatearFechaParaIndice(fechaInicioDate);

    console.log(
      `📅 FECHA INICIO CORREGIDA: ${fechaInicio} → ${fechaInicioSinTZ} → ${mesInicio}`
    );

    // 1. OBTENER ÍNDICE INICIAL (del mes de inicio del presupuesto)
    const indiceInicial = await obtenerIndicePorFecha(
      mesInicio,
      metodoActualizacion,
      tipo
    );

    if (!indiceInicial) {
      if (esMetodoPersonalizado(metodoActualizacion)) {
        console.log(
          `⚠️ Método personalizado "${metodoActualizacion}" sin valor para ${mesInicio} - USANDO VALOR POR DEFECTO`
        );

        const indiceDefecto = 1000;

        return {
          seActualiza: true,
          metodoActualizacion: metodoActualizacion,
          requiereConfiguracion: true,
          indiceDefectoUsado: indiceDefecto,
          evolucion: [
            {
              mes: "Configuración Pendiente",
              fecha: mesInicio,
              observaciones: `Sistema "${metodoActualizacion}" requiere configuración de índices. Se usa valor temporal de ${indiceDefecto}.`,
            },
          ],
          resumenFinal: {
            montoActualizado: parseFloat(montoInicial),
            pagosAcumulados: pagos.reduce(
              (sum, pago) =>
                sum +
                convertirMonto(
                  pago.monto,
                  pago.moneda,
                  monedaInicial,
                  pago.tipo_cambio
                ),
              0
            ),
            saldoPendiente:
              parseFloat(montoInicial) -
              pagos.reduce(
                (sum, pago) =>
                  sum +
                  convertirMonto(
                    pago.monto,
                    pago.moneda,
                    monedaInicial,
                    pago.tipo_cambio
                  ),
                0
              ),
            unidades: parseFloat(montoInicial) / indiceDefecto,
            ultimoIndiceUsado: indiceDefecto,
            totalUnidades: parseFloat(montoInicial) / indiceDefecto,
            unidadesGastadas: 0,
          },
        };
      } else {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE CAC OFICIAL para ${mesInicio}`);
        return {
          error: `No se encontró índice CAC para ${mesInicio} con el método ${metodoActualizacion}.`,
        };
      }
    }

    console.log(
      `📊 ÍNDICE INICIAL (${mesInicio}): ${indiceInicial.toFixed(2)}`
    );

    // 2. CONVERTIR PRESUPUESTO A UNIDADES
    const unidadesSistema = parseFloat(montoInicial) / indiceInicial;
    console.log(`🔢 UNIDADES SISTEMA: ${unidadesSistema.toFixed(2)} unidades`);

    // 3. PROCESAR PAGOS MES A MES
    const evolucion = [];
    const pagosPorMes = agruparPagosPorMes(pagos, monedaInicial);

    let unidadesGastadas = 0;
    const fechaHoy = new Date();
    let mesActual = new Date(
      fechaInicioDate.getFullYear(),
      fechaInicioDate.getMonth(),
      1
    );

    // OBTENER EL ÚLTIMO ÍNDICE REAL DISPONIBLE PARA CONSISTENCIA
    const ultimoIndiceReal = await obtenerUltimoIndiceRealDisponible(
      metodoActualizacion,
      tipo
    );

    while (mesActual <= fechaHoy) {
      const mesString = formatearFechaParaIndice(mesActual);
      const pagosDelMes = pagosPorMes[mesString] || [];

      // Índice para este mes
      let indiceDelMes = await obtenerIndicePorFecha(
        mesString,
        metodoActualizacion,
        tipo
      );
      let esProvisorio = false;

      // Si no hay índice para este mes, usar el último real disponible (CONSISTENTE)
      if (!indiceDelMes) {
        indiceDelMes = ultimoIndiceReal;
        esProvisorio = true;
      }

      if (!indiceDelMes) {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE para ${mesString}`);
        break;
      }

      // Convertir pagos del mes a unidades
      let unidadesDelMes = 0;
      const pagosDetallados = pagosDelMes.map((pago) => {
        const unidadesPago = pago.montoConvertido / indiceDelMes;
        unidadesDelMes += unidadesPago;
        return {
          ...pago,
          indiceUsado: indiceDelMes,
          unidades: unidadesPago,
          esProvisorio: esProvisorio,
        };
      });

      unidadesGastadas += unidadesDelMes;

      // Saldo en unidades y en pesos
      const unidadesRestantes = unidadesSistema - unidadesGastadas;
      const saldoEnPesos = unidadesRestantes * indiceDelMes;

      evolucion.push({
        mes: formatearMesAno(mesActual),
        fecha: mesString,
        indiceUsado: indiceDelMes,
        esProvisorio: esProvisorio,
        unidadesDelMes: unidadesDelMes,
        unidadesGastadas: unidadesGastadas,
        unidadesRestantes: unidadesRestantes,
        saldoEnPesos: saldoEnPesos,
        pagosDelMes: pagosDetallados,
        cantidadPagos: pagosDelMes.length,
        observaciones: esProvisorio
          ? `Índice provisorio (último disponible)`
          : `Índice oficial del mes`,
      });

      mesActual.setMonth(mesActual.getMonth() + 1);
    }

    // 4. CALCULAR TOTALES FINALES
    const ultimoIndiceDisponible = ultimoIndiceReal;

    // Recalcular saldo final con el último índice disponible
    const unidadesFinales = unidadesSistema - unidadesGastadas;
    const saldoFinalEnPesos = unidadesFinales * ultimoIndiceDisponible;
    const totalPagadoEnPesos = pagos.reduce((sum, pago) => {
      return (
        sum +
        convertirMonto(pago.monto, pago.moneda, monedaInicial, pago.tipo_cambio)
      );
    }, 0);

    console.log(`💰 RESULTADO FINAL:`, {
      unidadesSistema: unidadesSistema.toFixed(2),
      unidadesGastadas: unidadesGastadas.toFixed(2),
      unidadesRestantes: unidadesFinales.toFixed(2),
      ultimoIndice: ultimoIndiceDisponible.toFixed(2),
      saldoFinalEnPesos: saldoFinalEnPesos.toFixed(0),
    });

    return {
      unidadesSistema: unidadesSistema,
      ultimoIndiceDisponible: ultimoIndiceDisponible,
      evolucion: evolucion,
      resumenFinal: {
        montoActualizado: unidadesFinales * ultimoIndiceDisponible,
        pagosAcumulados: totalPagadoEnPesos,
        saldoPendiente: saldoFinalEnPesos,
        unidades: unidadesFinales,
        ultimoIndiceUsado: ultimoIndiceDisponible,
        totalUnidades: unidadesSistema,
        unidadesGastadas: unidadesGastadas,
      },
    };
  };

  // FUNCIÓN: OBTENER ÍNDICE POR FECHA (CAC oficial o personalizado)
  const obtenerIndicePorFecha = async (fecha, metodo, tipo) => {
    if (!metodo) return null;

    console.log(
      `🔍 BUSCANDO ÍNDICE: fecha=${fecha}, metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    // PRIMERO: Buscar si hay valor personalizado (override)
    try {
      const { data: valorPersonalizado, error } = await supabase
        .from("valores_personalizados_actualizacion")
        .select("coeficiente")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo", tipo)
        .eq("fecha_aplicacion", fecha)
        .maybeSingle();

      if (!error && valorPersonalizado) {
        console.log(
          `🎛️ VALOR PERSONALIZADO encontrado para ${fecha}:`,
          valorPersonalizado.coeficiente
        );
        return parseFloat(valorPersonalizado.coeficiente);
      }
    } catch (err) {
      console.error("Error buscando valor personalizado:", err);
    }

    // Si es método personalizado pero no hay valor, devolver null
    if (esMetodoPersonalizado(metodo)) {
      console.log(
        `⚠️ Método personalizado "${metodo}" pero no hay valor configurado para ${fecha}`
      );
      return null;
    }

    // Buscar en índices CAC oficiales
    const indice = indicesCAC.find((i) => i.fecha === fecha);
    if (!indice) {
      console.log(`❌ No hay CAC oficial para ${fecha}`);
      return null;
    }

    let valor = null;
    switch (metodo) {
      case "CAC_GENERAL":
        valor = parseFloat(indice.cac_general);
        break;
      case "CAC_MATERIALES":
        valor = parseFloat(indice.cac_materiales);
        break;
      case "CAC_MANO_OBRA":
        valor = parseFloat(indice.cac_mano_obra);
        break;
    }

    console.log(`📊 CAC OFICIAL para ${fecha} (${metodo}): ${valor}`);
    return valor;
  };

  // FUNCIÓN: OBTENER ÚLTIMO ÍNDICE REAL DISPONIBLE
  const obtenerUltimoIndiceRealDisponible = async (metodo, tipo) => {
    console.log(
      `🔍 BUSCANDO ÚLTIMO ÍNDICE DISPONIBLE: metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    if (esMetodoPersonalizado(metodo)) {
      // Para personalizados, buscar el último valor cargado
      try {
        const { data: ultimoPersonalizado, error } = await supabase
          .from("valores_personalizados_actualizacion")
          .select("coeficiente, fecha_aplicacion")
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo", tipo)
          .order("fecha_aplicacion", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && ultimoPersonalizado) {
          console.log(
            `🔍 ÚLTIMO PERSONALIZADO: ${ultimoPersonalizado.fecha_aplicacion} = ${ultimoPersonalizado.coeficiente}`
          );
          return parseFloat(ultimoPersonalizado.coeficiente);
        }
      } catch (err) {
        console.error("Error buscando último personalizado:", err);
      }

      console.log(
        `❌ NO SE ENCONTRÓ NINGÚN VALOR PERSONALIZADO para método ${metodo}`
      );
      return null;
    }

    // Para CAC, buscar el último disponible
    const indicesOrdenados = [...indicesCAC].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );

    for (const indice of indicesOrdenados) {
      let valor = null;
      switch (metodo) {
        case "CAC_GENERAL":
          valor = parseFloat(indice.cac_general);
          break;
        case "CAC_MATERIALES":
          valor = parseFloat(indice.cac_materiales);
          break;
        case "CAC_MANO_OBRA":
          valor = parseFloat(indice.cac_mano_obra);
          break;
      }

      if (valor) {
        console.log(
          `🔍 ÚLTIMO CAC DISPONIBLE: ${indice.fecha} = ${valor.toFixed(2)}`
        );
        return valor;
      }
    }

    console.error(`❌ NO SE ENCONTRÓ NINGÚN ÍNDICE CAC DISPONIBLE`);
    return null;
  };

  // FUNCIÓN: AGRUPAR PAGOS POR MES
  const agruparPagosPorMes = (pagos, monedaDestino) => {
    const grupos = {};

    pagos.forEach((pago) => {
      const fechaPago = new Date(
        formatearFechaSinTimezone(pago.fecha) + "T00:00:00"
      );
      const mesString = formatearFechaParaIndice(fechaPago);

      if (!grupos[mesString]) {
        grupos[mesString] = [];
      }

      grupos[mesString].push({
        ...pago,
        montoConvertido: convertirMonto(
          pago.monto,
          pago.moneda,
          monedaDestino,
          pago.tipo_cambio
        ),
      });
    });

    return grupos;
  };

  const formatearFechaParaIndice = (fecha) => {
    return fecha.toISOString().split("T")[0].substring(0, 7) + "-01";
  };

  const formatearMesAno = (fecha) => {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "short",
    }).format(fecha);
  };

  // ✅ NUEVA FUNCIÓN: Cargar fecha de finalización desde cronograma
  const cargarFechaFinalizacionCronograma = async () => {
    try {
      // Obtener todas las tareas del cronograma del proyecto
      const { data: tareas, error } = await supabase
        .from("gantt_tareas")
        .select("fecha_inicio, fecha_fin")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("activo", true)
        .is("deleted_at", null);

      if (error) {
        console.error("Error cargando tareas del cronograma:", error);
        return;
      }

      if (!tareas || tareas.length === 0) {
        setFechaFinalizacionCronograma(null);
        setDuracionTotalProyecto(null);
        return;
      }

      // Encontrar la fecha más tardía de finalización
      const fechasFinales = tareas.map((t) => new Date(t.fecha_fin));
      const fechaFinalizacionMasTarde = new Date(Math.max(...fechasFinales));

      // Encontrar la fecha más temprana de inicio
      const fechasIniciales = tareas.map((t) => new Date(t.fecha_inicio));
      const fechaInicioMasTemprana = new Date(Math.min(...fechasIniciales));

      // Calcular duración total del proyecto
      const duracionDias = Math.ceil(
        (fechaFinalizacionMasTarde - fechaInicioMasTemprana) /
          (1000 * 60 * 60 * 24)
      );

      let duracionTexto = "";
      if (duracionDias < 30) {
        duracionTexto = `${duracionDias} días`;
      } else if (duracionDias < 365) {
        const meses = Math.floor(duracionDias / 30);
        const diasRestantes = duracionDias % 30;
        if (diasRestantes > 0) {
          duracionTexto = `${meses} meses y ${diasRestantes} días`;
        } else {
          duracionTexto = `${meses} meses`;
        }
      } else {
        const años = Math.floor(duracionDias / 365);
        const mesesRestantes = Math.floor((duracionDias % 365) / 30);
        if (mesesRestantes > 0) {
          duracionTexto = `${años} años y ${mesesRestantes} meses`;
        } else {
          duracionTexto = `${años} años`;
        }
      }

      setFechaFinalizacionCronograma(fechaFinalizacionMasTarde);
      setDuracionTotalProyecto(duracionTexto);

      console.log(
        "📅 FECHA FINALIZACIÓN CRONOGRAMA:",
        fechaFinalizacionMasTarde
      );
      console.log("⏱️ DURACIÓN TOTAL PROYECTO:", duracionTexto);
    } catch (err) {
      console.error("Error calculando fecha de finalización:", err);
      setFechaFinalizacionCronograma(null);
      setDuracionTotalProyecto(null);
    }
  };

  // ✅ NUEVA FUNCIÓN: Cargar estado financiero basado en presupuestos iniciales
  // ✅ NUEVA FUNCIÓN: Cargar estado financiero basado en presupuestos iniciales CON ACTUALIZACIONES
  const cargarEstadoFinanciero = async () => {
    try {
      // ✅ RESETEAR ESTADO AL INICIO PARA EVITAR RENDERS INTERMEDIOS
      setEstadoFinancieroListo(false);

      let totalPresupuestadoActualizado = 0;
      let totalPresupuestadoInicial = 0;
      let totalPagado = 0;
      let comprometidoReal = 0;
      let saldoHonorarios = 0;
      let saldoManoObra = 0;

      const proyecto = proyectoSeleccionado;

      // ========================================
      // 1. HONORARIOS Y MANO DE OBRA CON ACTUALIZACIONES (YA IMPLEMENTADO CORRECTAMENTE)
      // ========================================
      if (proyecto.tipo_obra === "llave_en_mano") {
        // LLAVE EN MANO - usar datos actualizados
        if (datosLlaveEnMano && !datosLlaveEnMano.error) {
          console.log("🏗️ PROCESANDO LLAVE EN MANO ACTUALIZADA...");

          // ✅ USAR DATOS ACTUALIZADOS (total_pagado + saldo_pendiente)
          const totalActualizadoLlave =
            datosLlaveEnMano.total_pagado + datosLlaveEnMano.saldo_pendiente;

          // ✅ CALCULAR PRESUPUESTO INICIAL (SIN ACTUALIZACIONES)
          const presupuestoInicialLlave = convertirMonto(
            datosLlaveEnMano.montoInicial, // ← MONTO ORIGINAL SIN ACTUALIZAR
            datosLlaveEnMano.monedaInicial,
            monedaVista,
            cotizacionDolar
          );
          totalPresupuestadoInicial += presupuestoInicialLlave;

          const montoConvertidoLlave = convertirMonto(
            totalActualizadoLlave,
            datosLlaveEnMano.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPresupuestadoActualizado += montoConvertidoLlave;

          const totalPagadoLlave = convertirMonto(
            datosLlaveEnMano.total_pagado,
            datosLlaveEnMano.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPagado += totalPagadoLlave;

          saldoHonorarios = convertirMonto(
            datosLlaveEnMano.saldo_pendiente,
            datosLlaveEnMano.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          console.log("💰 LLAVE EN MANO ACTUALIZADA:", {
            totalActualizado: totalActualizadoLlave,
            presupuestadoConvertido: montoConvertidoLlave,
            pagadoConvertido: totalPagadoLlave,
            saldoConvertido: saldoHonorarios,
          });
        }
      } else {
        // ADMINISTRACIÓN: HONORARIOS + MANO DE OBRA

        // HONORARIOS ACTUALIZADOS
        if (
          datosHonorarios &&
          !datosHonorarios.error &&
          datosHonorarios.total_pagado !== undefined
        ) {
          console.log("👨‍💼 PROCESANDO HONORARIOS ACTUALIZADOS...");

          // ✅ USAR DATOS ACTUALIZADOS (total_pagado + saldo_pendiente)
          const totalActualizadoHonorarios =
            datosHonorarios.total_pagado + datosHonorarios.saldo_pendiente;

          // ✅ CALCULAR PRESUPUESTO INICIAL (SIN ACTUALIZACIONES)
          const presupuestoInicialHonorarios = convertirMonto(
            datosHonorarios.montoInicial, // ← MONTO ORIGINAL SIN ACTUALIZAR
            datosHonorarios.monedaInicial,
            monedaVista,
            cotizacionDolar
          );
          totalPresupuestadoInicial += presupuestoInicialHonorarios;

          const montoConvertidoHonorarios = convertirMonto(
            totalActualizadoHonorarios,
            datosHonorarios.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPresupuestadoActualizado += montoConvertidoHonorarios;

          const totalPagadoHonorarios = convertirMonto(
            datosHonorarios.total_pagado,
            datosHonorarios.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPagado += totalPagadoHonorarios;

          saldoHonorarios = convertirMonto(
            datosHonorarios.saldo_pendiente,
            datosHonorarios.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          console.log("💰 HONORARIOS ACTUALIZADOS:", {
            totalActualizado: totalActualizadoHonorarios,
            presupuestadoConvertido: montoConvertidoHonorarios,
          });
        }

        // MANO DE OBRA ACTUALIZADA
        if (
          proyecto.mano_obra_separada &&
          datosManoObra &&
          !datosManoObra.error &&
          datosManoObra.total_pagado !== undefined
        ) {
          console.log("👷‍♂️ PROCESANDO MANO DE OBRA ACTUALIZADA...");

          // ✅ USAR DATOS ACTUALIZADOS (total_pagado + saldo_pendiente)
          const totalActualizadoMO =
            datosManoObra.total_pagado + datosManoObra.saldo_pendiente;

          // ✅ CALCULAR PRESUPUESTO INICIAL (SIN ACTUALIZACIONES)
          const presupuestoInicialMO = convertirMonto(
            datosManoObra.montoInicial, // ← MONTO ORIGINAL SIN ACTUALIZAR
            datosManoObra.monedaInicial,
            monedaVista,
            cotizacionDolar
          );
          totalPresupuestadoInicial += presupuestoInicialMO;

          const montoConvertidoMO = convertirMonto(
            totalActualizadoMO,
            datosManoObra.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPresupuestadoActualizado += montoConvertidoMO;

          const totalPagadoMO = convertirMonto(
            datosManoObra.total_pagado,
            datosManoObra.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          totalPagado += totalPagadoMO;

          saldoManoObra = convertirMonto(
            datosManoObra.saldo_pendiente,
            datosManoObra.monedaInicial,
            monedaVista,
            cotizacionDolar
          );

          console.log("💰 MANO DE OBRA ACTUALIZADA:", {
            totalActualizado: totalActualizadoMO,
            presupuestadoConvertido: montoConvertidoMO,
          });
        }
      }

      // ========================================
      // 2. PRESUPUESTOS MANUALES - ✅ CORREGIDO: USAR MONTO_ACTUALIZADO
      // ========================================
      console.log(
        "📊 PROCESANDO PRESUPUESTOS MANUALES CON ACTUALIZACION CAC..."
      );

      const { data: presupuestos, error: errorPresupuestos } = await supabase
        .from("presupuestos_iniciales")
        .select("*")
        .eq("proyecto_id", proyecto.id);

      if (!errorPresupuestos && presupuestos?.length) {
        console.log(
          "📋 Presupuestos manuales encontrados:",
          presupuestos.length
        );

        // ✅ ACTUALIZAR PRESUPUESTOS CON CAC ANTES DE USAR
        const presupuestosActualizados = await Promise.all(
          presupuestos.map((presupuesto) =>
            actualizarPresupuestoConCAC(presupuesto, indicesCAC)
          )
        );

        // ✅ CALCULAR PRESUPUESTO INICIAL TOTAL (SIN ACTUALIZACIONES)
        let totalPresupuestoInicialTemp = 0;

        // ✅ SUMAR PRESUPUESTOS MANUALES USANDO MONTO_ACTUALIZADO
        for (const presupuesto of presupuestosActualizados) {
          // ✅ CALCULAR PRESUPUESTO INICIAL (SIN ACTUALIZACIONES)
          const montoInicial = convertirMonto(
            presupuesto.monto_presupuestado, // ← MONTO ORIGINAL SIN ACTUALIZAR
            presupuesto.moneda,
            monedaVista,
            presupuesto.tipo_cambio
          );
          totalPresupuestoInicialTemp += montoInicial;

          // ✅ USAR MONTO_ACTUALIZADO SI EXISTE, SINO MONTO_PRESUPUESTADO
          const montoAUsar =
            presupuesto.monto_actualizado || presupuesto.monto_presupuestado;

          const montoPresupuesto = convertirMonto(
            montoAUsar, // ← CORREGIDO: Usar monto actualizado
            presupuesto.moneda,
            monedaVista,
            presupuesto.tipo_cambio
          );

          totalPresupuestadoActualizado += montoPresupuesto;

          console.log("📋 PRESUPUESTO MANUAL (ACTUALIZADO):", {
            descripcion: presupuesto.descripcion,
            montoOriginal: presupuesto.monto_presupuestado,
            montoActualizado: presupuesto.monto_actualizado,
            montoUsado: montoAUsar,
            factorActualizacion: presupuesto.factor_actualizacion,
            observacion: presupuesto.observacion_actualizacion,
            montoConvertido: montoPresupuesto,
          });
        }

        // Obtener gastos vinculados a presupuestos manuales (SIN CAMBIOS)
        const { data: gastos } = await supabase
          .from("salidas")
          .select("monto, moneda, tipo_cambio, presupuesto_inicial_id")
          .eq("proyecto_id", proyecto.id)
          .not("presupuesto_inicial_id", "is", null);

        if (gastos) {
          for (const gasto of gastos) {
            const montoGasto = convertirMonto(
              gasto.monto,
              gasto.moneda,
              monedaVista,
              gasto.tipo_cambio
            );
            totalPagado += montoGasto;
          }
        }

        // ✅ AGREGAR PRESUPUESTO INICIAL DE PRESUPUESTOS MANUALES AL TOTAL
        totalPresupuestadoInicial += totalPresupuestoInicialTemp;

        console.log("💰 PRESUPUESTOS MANUALES TOTAL (CON ACTUALIZACION CAC):", {
          presupuestadoInicial: totalPresupuestoInicialTemp,
          presupuestadoTotal: presupuestosActualizados.reduce((sum, p) => {
            const montoAUsar = p.monto_actualizado || p.monto_presupuestado;
            return (
              sum +
              convertirMonto(montoAUsar, p.moneda, monedaVista, p.tipo_cambio)
            );
          }, 0),
          pagadoTotal:
            gastos?.reduce(
              (sum, g) =>
                sum +
                convertirMonto(g.monto, g.moneda, monedaVista, g.tipo_cambio),
              0
            ) || 0,
        });
      }

      // ========================================
      // 3. COMPROMETIDO REAL - REPLICAR EXACTAMENTE LA LÓGICA DE PRESUPUESTOS.JS
      // ========================================
      console.log("📋 PROCESANDO COMPROMETIDO REAL (replicando Presupuestos.js)...");

      const { data: presupuestosReales, error: errorPresupuestosReales } =
        await supabase
          .from("presupuestos")
          .select("*")
          .eq("proyecto_id", proyecto.id)
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null)
          .in("estado", ["pendiente", "aprobado", "en_ejecucion"]);

      if (!errorPresupuestosReales && presupuestosReales?.length) {
        console.log(
          "💼 Presupuestos reales encontrados:",
          presupuestosReales.length
        );

        // Replicar exactamente la lógica de Presupuestos.js
        const presupuestosConSaldos = await Promise.all(
          presupuestosReales.map(async (presupuesto) => {
            try {
              // Llamar a las mismas funciones RPC que usa Presupuestos.js
              const { data: totalData } = await supabase.rpc(
                "calcular_total_presupuesto",
                { presupuesto_uuid: presupuesto.id }
              );

              const { data: saldoData } = await supabase.rpc(
                "calcular_saldo_presupuesto",
                { presupuesto_uuid: presupuesto.id }
              );

              // Si las funciones RPC no existen, calcular manualmente como en Presupuestos.js
              let saldoCalculado;
              if (!saldoData || !saldoData[0]) {
                // Obtener adicionales
                const { data: adicionales } = await supabase
                  .from("adicionales_presupuesto")
                  .select("monto, destino_adicional, aplica_cliente")
                  .eq("presupuesto_id", presupuesto.id)
                  .eq("activo", true)
                  .is("deleted_at", null);

                const totalAdicionales = (adicionales || []).reduce((sum, adicional) => {
                  if (presupuesto.tiene_doble_monto) {
                    if (adicional.aplica_cliente || adicional.destino_adicional === 'cliente' || adicional.destino_adicional === 'ambos') {
                      return sum + parseFloat(adicional.monto || 0);
                    }
                    return sum;
                  } else {
                    return sum + parseFloat(adicional.monto || 0);
                  }
                }, 0);

                // Obtener pagos
                const { data: pagosPresupuesto } = await supabase
                  .from("salidas")
                  .select("monto, moneda, tipo_cambio, activo, deleted_at")
                  .eq("presupuesto_id", presupuesto.id)
                  .eq("activo", true)
                  .is("deleted_at", null);

                const totalPagado = (pagosPresupuesto || []).reduce((sum, pago) => {
                  const montoConvertido = convertirMonto(
                    pago.monto,
                    pago.moneda,
                    presupuesto.moneda,
                    pago.tipo_cambio
                  );
                  return sum + montoConvertido;
                }, 0);

                const montoTotal = parseFloat(presupuesto.monto_base) + totalAdicionales;
                saldoCalculado = {
                  total_presupuesto: montoTotal,
                  total_pagado: totalPagado,
                  saldo_pendiente: Math.max(0, montoTotal - totalPagado)
                };
              } else {
                saldoCalculado = saldoData[0];
              }

              return {
                ...presupuesto,
                saldo_data: saldoCalculado
              };
            } catch (err) {
              console.error("❌ Error procesando presupuesto:", presupuesto.id, err);
              return {
                ...presupuesto,
                saldo_data: {
                  total_presupuesto: presupuesto.monto_base,
                  total_pagado: 0,
                  saldo_pendiente: presupuesto.monto_base
                }
              };
            }
          })
        );

        // Sumar los saldos pendientes convertidos a la moneda de vista
        for (const presupuesto of presupuestosConSaldos) {
          const saldoPendiente = parseFloat(presupuesto.saldo_data?.saldo_pendiente || 0);
          
          if (saldoPendiente > 0) {
            const saldoConvertido = convertirMonto(
              saldoPendiente,
              presupuesto.moneda,
              monedaVista,
              cotizacionDolar // Usar dólar oficial para pendientes
            );

            comprometidoReal += saldoConvertido;
          }
        }

        console.log("💰 COMPROMETIDO REAL TOTAL (replicando Presupuestos.js):", comprometidoReal);
        console.log("🔍 Presupuestos con saldos:", presupuestosConSaldos.map(p => ({
          id: p.id,
          moneda: p.moneda,
          saldoPendiente: p.saldo_data?.saldo_pendiente,
          saldoConvertido: convertirMonto(
            p.saldo_data?.saldo_pendiente || 0,
            p.moneda,
            monedaVista,
            cotizacionDolar
          )
        })));
      } else {
        console.log(
          "📝 No hay presupuestos reales en estados pendiente/aprobado/en_ejecucion"
        );
      }

      // ========================================
      // 4. CONSTRUIR DATOS PARA EL GRÁFICO (SIN CAMBIOS)
      // ========================================

      const datos = [];

      // PAGADO
      if (totalPagado > 0) {
        datos.push({
          name: "Pagado",
          value: totalPagado,
          color: "#ef4444",
        });
      }

      // COMPROMETIDO REAL
      if (comprometidoReal > 0) {
        datos.push({
          name: "Comprometido Gastos",
          value: comprometidoReal,
          color: "#f59e0b",
        });
      }

      // SALDO HONORARIOS/LLAVE EN MANO
      if (saldoHonorarios > 0) {
        const nombreSaldo =
          proyecto.tipo_obra === "llave_en_mano"
            ? "Comprometido Obra"
            : "Comprometido Hon.";
        datos.push({
          name: nombreSaldo,
          value: saldoHonorarios,
          color: "#8b5cf6",
        });
      }

      // SALDO MANO DE OBRA
      if (saldoManoObra > 0) {
        datos.push({
          name: "Comprometido M.O.",
          value: saldoManoObra,
          color: "#06b6d4",
        });
      }

      // DISPONIBLE o SOBREGASTO
      const totalComprometido =
        comprometidoReal + saldoHonorarios + saldoManoObra;
      const totalEjecutado = totalPagado + totalComprometido;

      if (totalEjecutado < totalPresupuestadoActualizado) {
        const disponible = totalPresupuestadoActualizado - totalEjecutado;
        if (disponible > 0) {
          datos.push({
            name: "Disponible",
            value: disponible,
            color: "#10b981",
          });
        }
      } else if (totalEjecutado > totalPresupuestadoActualizado) {
        const sobregasto = totalEjecutado - totalPresupuestadoActualizado;
        datos.push({
          name: "Sobregasto",
          value: sobregasto,
          color: "#dc2626",
        });
      }

      // Filtrar valores cero
      const datosFiltrados = datos.filter((item) => item.value > 0);

      // ✅ LOS TOTALES YA ESTÁN CARGADOS EN EL ESTADO, NO NECESITAMOS CONSULTAR DE NUEVO
      setDatosFinancieros(datosFiltrados);
      setEstadoFinancieroListo(true); // ✅ MARCAR COMO LISTO
    } catch (err) {
      console.error("❌ Error cargando estado financiero:", err);
      setDatosFinancieros([]);
    }
  };

  const cargarInfoObra = () => {
    const proyecto = proyectoSeleccionado;
    const fechaInicio = proyecto.fecha_inicio
      ? new Date(proyecto.fecha_inicio)
      : null;
    const fechaFin = proyecto.fecha_finalizacion
      ? new Date(proyecto.fecha_finalizacion)
      : null;
    const hoy = new Date();

    let tiempoTranscurrido = "";
    if (fechaInicio) {
      const fechaReferencia =
        proyecto.obra_finalizada && fechaFin ? fechaFin : hoy;
      const diffTime = Math.abs(fechaReferencia - fechaInicio);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 30) {
        tiempoTranscurrido = `${diffDays} días`;
      } else {
        const meses = Math.floor(diffDays / 30);
        const diasRestantes = diffDays % 30;
        if (diasRestantes > 0) {
          tiempoTranscurrido = `${meses} meses y ${diasRestantes} días`;
        } else {
          tiempoTranscurrido = `${meses} meses`;
        }
      }
    } else {
      tiempoTranscurrido = "No definido";
    }

    setInfoObra({
      apellidoCliente: proyecto.apellido_cliente,
      ubicacion: proyecto.ubicacion,
      metrosComputo: proyecto.metros_computo,
      fechaInicio: fechaInicio,
      tiempoTranscurrido,
      obraFinalizada: proyecto.obra_finalizada,
    });
  };

  const cargarDineroCajas = async () => {
    try {
      const { data, error } = await supabase
        .from("cajas")
        .select("moneda, balance_actual")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo_caja", "proyecto");

      if (!error && data) {
        const totales = { ARS: 0, USD: 0 };
        data.forEach((caja) => {
          totales[caja.moneda] += parseFloat(caja.balance_actual || 0);
        });
        setDineroCajas(totales);
      }
    } catch (err) {
      console.error("Error cargando dinero en cajas:", err);
    }
  };

  const convertirMonto = (
    monto,
    monedaOriginal,
    monedaDestino,
    tipoCambio = null
  ) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOriginal === monedaDestino) {
      return montoNum;
    }

    const tcNum = parseFloat(tipoCambio || cotizacionDolar || 1000);

    if (monedaDestino === "ARS" && monedaOriginal === "USD") {
      return montoNum * tcNum;
    }

    if (monedaDestino === "USD" && monedaOriginal === "ARS") {
      return montoNum / tcNum;
    }

    return montoNum;
  };

  // ✅ NUEVO: Función para convertir monto con tipo de cambio del proyecto (copiada de PresupuestosIniciales.js)
  const convertirMontoConTCProyecto = (monto, monedaOrigen, monedaDestino) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOrigen === monedaDestino) {
      return montoNum;
    }

    const tcProyecto =
      proyectoSeleccionado?.tipo_cambio || cotizacionDolar || 1000;

    if (monedaOrigen === "USD" && monedaDestino === "ARS") {
      return montoNum * tcProyecto;
        } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
      return montoNum / tcProyecto;
    }

    return montoNum;
  };

  // ✅ NUEVO: Función para cargar presupuestos iniciales
  const cargarPresupuestosIniciales = async () => {
    if (!proyectoSeleccionado) return;

    try {
      const { data, error } = await supabase
        .from("presupuestos_iniciales")
        .select("*")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error cargando presupuestos iniciales:", error);
      } else {
        setPresupuestosIniciales(data || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  // ✅ SIMPLE: Función para obtener ambos totales desde las tablas
  const obtenerTotalesDesdeTablas = async () => {
    if (!proyectoSeleccionado) return { totalInicial: 0, totalActualizado: 0 };

    console.log("📊 CONSULTANDO TABLAS UNA SOLA VEZ...");

    try {
      // Consulta directa y simple
      const { data: inicial, error: errorInicial } = await supabase
        .from("presupuestos_totales_iniciales")
        .select(monedaVista === "USD" ? "total_usd" : "total_ars")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .single();

      const { data: actualizado, error: errorActualizado } = await supabase
        .from("presupuestos_totales_actualizados")
        .select(monedaVista === "USD" ? "total_usd" : "total_ars")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .single();

      const totalInicial =
        inicial?.[monedaVista === "USD" ? "total_usd" : "total_ars"] || 0;
      const totalActualizado =
        actualizado?.[monedaVista === "USD" ? "total_usd" : "total_ars"] || 0;

      console.log(
        `✅ Total Inicial ${monedaVista}: ${totalInicial.toLocaleString()}`
      );
      console.log(
        `✅ Total Actualizado ${monedaVista}: ${totalActualizado.toLocaleString()}`
      );

      return { totalInicial, totalActualizado };
    } catch (error) {
      console.error("❌ Error consultando tablas:", error);
      return { totalInicial: 0, totalActualizado: 0 };
    }
  };

  const cargarGastoTotal = async () => {
    try {
      let gastoTotal = 0;

      // 1. Salidas desde cajas proyecto (EXCLUYENDO ADICIONALES)
      const { data: salidas, error: errorSalidas } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, es_adicional, cajas!inner(tipo_caja)")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .eq("cajas.tipo_caja", "proyecto")
        .eq("es_adicional", false); // ✅ EXCLUIR ADICIONALES

      if (!errorSalidas && salidas) {
        salidas.forEach((salida) => {
          gastoTotal += convertirMonto(
            salida.monto,
            salida.moneda,
            monedaVista,
            salida.tipo_cambio
          );
        });
      }

      // 2. Salidas directas del cliente (EXCLUYENDO ADICIONALES)
      const { data: salidasCliente, error: errorSalidasCliente } =
        await supabase
          .from("salidas")
          .select("monto, moneda, tipo_cambio, es_adicional")
          .eq("constructora_id", constructora.id)
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq('activo', true)
          .is('deleted_at', null)
          .is("caja_origen_id", null)
          .eq("es_adicional", false); // ✅ EXCLUIR ADICIONALES

      if (!errorSalidasCliente && salidasCliente) {
        salidasCliente.forEach((salida) => {
          gastoTotal += convertirMonto(
            salida.monto,
            salida.moneda,
            monedaVista,
            salida.tipo_cambio
          );
        });
      }

      // 3. EXCLUIR entradas de honorarios por adicionales del gasto total
      // Obtener el concepto de honorarios por adicionales para excluirlo
      const { data: conceptoHonorariosAdicionales } = await supabase
        .from("conceptos_entrada")
        .select("id")
        .or("nombre.eq.Honorarios por Adicionales,nombre.eq.Honorarios por adicionales,nombre.ilike.%honorarios%adicional%")
        .single();

      // 4. Entradas hacia cajas mano_obra (corregido)
      if (proyectoSeleccionado.mano_obra_separada) {
        // Obtener los IDs de las cajas de mano de obra
        const { data: cajasManoObra } = await supabase
          .from("cajas")
          .select("id")
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo_caja", "mano_obra");

        const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];

        if (idsCajasManoObra.length > 0) {
          // Traer todas las entradas del proyecto
          const { data: entradas, error: errorEntradas } = await supabase
            .from("entradas")
            .select("monto, moneda, tipo_cambio, caja_destino_id")
            .eq("proyecto_id", proyectoSeleccionado.id)
            .eq('activo', true)
            .is('deleted_at', null);

          if (!errorEntradas && entradas) {
            entradas
              .filter((entrada) =>
                idsCajasManoObra.includes(entrada.caja_destino_id)
              )
              .forEach((entrada) => {
                gastoTotal += convertirMonto(
                  entrada.monto,
                  entrada.moneda,
                  monedaVista,
                  entrada.tipo_cambio
                );
              });
          }
        }
      }

      // 5. Entradas con conceptos honorarios (EXCLUYENDO honorarios por adicionales)
      let filtroHonorarios = supabase
        .from("entradas")
        .select("monto, moneda, tipo_cambio, conceptos_entrada!inner(nombre)")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .ilike("conceptos_entrada.nombre", "%honorario%");

      // Excluir honorarios por adicionales si existe el concepto
      if (conceptoHonorariosAdicionales?.id) {
        filtroHonorarios = filtroHonorarios.neq("concepto_id", conceptoHonorariosAdicionales.id);
      }

      const { data: entradasHonorarios, error: errorHonorarios } = await filtroHonorarios;

      if (!errorHonorarios && entradasHonorarios) {
        entradasHonorarios.forEach((entrada) => {
          gastoTotal += convertirMonto(
            entrada.monto,
            entrada.moneda,
            monedaVista,
            entrada.tipo_cambio
          );
        });
      }

      const metrosComputo = parseFloat(
        proyectoSeleccionado.metros_computo || 1
      );

      setGastoTotal(gastoTotal);
      setGastoPorMetro(gastoTotal / metrosComputo);
    } catch (err) {
      console.error("Error calculando gasto total:", err);
    }
  };

  // ✅ FUNCIÓN AUXILIAR: Obtener índice por fecha
  const obtenerIndiceAdicionales = async (fecha, metodo, tipo) => {
    try {
      if (!metodo || !fecha) return null;

      const fechaFormateada = fecha.substring(0, 7) + "-01";
      
      console.log("🔍 DEBUG - Buscando índice:", { fecha, fechaFormateada, metodo, tipo });
      
      if (metodo === "PERSONALIZADO") {
        const { data: indicePersonalizado, error } = await supabase
          .from("indices_personalizados")
          .select("valor")
          .eq("fecha", fechaFormateada)
          .eq("metodo", proyectoSeleccionado.adicionales_nombre_metodo_personalizado || proyectoSeleccionado.nombre_metodo_personalizado_honorarios);
        
        if (error) {
          console.error("Error obteniendo índice personalizado:", error);
          return null;
        }
        
        console.log("🔍 DEBUG - Índice personalizado encontrado:", indicePersonalizado);
        return indicePersonalizado && indicePersonalizado.length > 0 ? indicePersonalizado[0].valor : null;
      } else {
        // Para CAC, usar el mismo patrón que funciona en HonorariosMO.js
        const { data: indiceCAC, error } = await supabase
          .from("indices_cac")
          .select("*")
          .eq("fecha", fechaFormateada)
          .eq("tipo", metodo);
        
        if (error) {
          console.error("Error obteniendo índice CAC:", error);
          return null;
        }
        
        console.log("🔍 DEBUG - Índice CAC encontrado:", indiceCAC);
        // Intentar diferentes nombres de columna que podrían existir
        if (indiceCAC && indiceCAC.length > 0) {
          const indice = indiceCAC[0];
          return indice.valor || indice.coeficiente || indice.factor || indice.indice || null;
        }
        return null;
      }
    } catch (error) {
      console.error("Error obteniendo índice:", error);
      return null;
    }
  };

  // ✅ NUEVA FUNCIÓN: Cargar datos de adicionales
  const cargarDatosAdicionales = async () => {
    console.log("🔍 DEBUG - Iniciando cargarDatosAdicionales", {
      proyectoId: proyectoSeleccionado?.id,
      adicionalesHabilitado: proyectoSeleccionado?.adicionales_honorarios_habilitado,
      proyectoCompleto: proyectoSeleccionado
    });
    
    try {
      // Verificar si el proyecto tiene adicionales habilitados
      if (!proyectoSeleccionado?.adicionales_honorarios_habilitado) {
        console.log("🔍 DEBUG - Adicionales NO habilitados, retornando null", {
          adicionales_honorarios_habilitado: proyectoSeleccionado?.adicionales_honorarios_habilitado,
          tipo: typeof proyectoSeleccionado?.adicionales_honorarios_habilitado
        });
        setDatosAdicionales(null);
        return;
      }

      // 1. Cargar gastos adicionales
      const { data: salidasAdicionales, error: errorSalidasAdicionales } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, fecha")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .eq("es_adicional", true);

      let totalGastosAdicionales = 0;
      if (!errorSalidasAdicionales && salidasAdicionales) {
        salidasAdicionales.forEach((salida) => {
          totalGastosAdicionales += convertirMonto(
            salida.monto,
            salida.moneda,
            monedaVista,
            salida.tipo_cambio
          );
        });
      }

      // 2. Calcular honorarios por adicionales (CON O SIN CAC) - SISTEMA DE UNIDADES
      let honorariosCalculados = 0;
      const monedaHonorarios = proyectoSeleccionado.adicionales_moneda_honorarios;
      const seActualiza = proyectoSeleccionado.adicionales_actualizar_con_indice;
      const metodoActualizacion = proyectoSeleccionado.adicionales_metodo_actualizacion;
      
      if (proyectoSeleccionado.adicionales_tipo_honorarios === "porcentaje") {
        const porcentaje = parseFloat(proyectoSeleccionado.adicionales_porcentaje_honorarios || 0);
        
        if (seActualiza && metodoActualizacion) {
          // CON CAC: Calcular UNIDADES por cada salida (igual que HonorariosMO)
          let unidadesTotales = 0;
          
          for (const salida of salidasAdicionales || []) {
            // 1. Convertir a moneda de honorarios
            const montoEnMonedaHonorarios = convertirMonto(
              salida.monto,
              salida.moneda,
              monedaHonorarios,
              salida.tipo_cambio
            );
            // 2. Aplicar porcentaje
            const honorarioSalida = montoEnMonedaHonorarios * (porcentaje / 100);
            // 3. Obtener índice de la fecha de la salida
            const fechaSalida = salida.fecha.substring(0, 7) + "-01";
            const indiceSalida = await obtenerIndiceAdicionales(fechaSalida, metodoActualizacion, "adicionales");
            // 4. Calcular unidades
            if (indiceSalida) {
              const unidadesSalida = honorarioSalida / indiceSalida;
              unidadesTotales += unidadesSalida;
            }
          }
          
          // 5. Convertir unidades a pesos con el último índice disponible
          const fechaHoy = new Date().toISOString().substring(0, 7) + "-01";
          const ultimoIndice = await obtenerIndiceAdicionales(fechaHoy, metodoActualizacion, "adicionales");
          
          if (ultimoIndice) {
            const honorariosEnMonedaHonorarios = unidadesTotales * ultimoIndice;
            honorariosCalculados = convertirMonto(honorariosEnMonedaHonorarios, monedaHonorarios, monedaVista, cotizacionDolar);
          }
        } else {
          // SIN CAC: Suma simple
          for (const salida of salidasAdicionales || []) {
            const montoEnMonedaHonorarios = convertirMonto(salida.monto, salida.moneda, monedaHonorarios, salida.tipo_cambio);
            const honorarioSalida = montoEnMonedaHonorarios * (porcentaje / 100);
            const honorarioEnMonedaVista = convertirMonto(honorarioSalida, monedaHonorarios, monedaVista, cotizacionDolar);
            honorariosCalculados += honorarioEnMonedaVista;
          }
        }
      } else if (proyectoSeleccionado.adicionales_tipo_honorarios === "monto_fijo") {
        // Monto fijo (con o sin CAC se maneja igual en Dashboard por simplicidad)
        const montoFijo = parseFloat(proyectoSeleccionado.adicionales_monto_fijo_honorarios || 0);
        honorariosCalculados = convertirMonto(montoFijo, monedaHonorarios, monedaVista, cotizacionDolar);
      }

      // 3. Cargar honorarios pagados
      const { data: conceptosEntrada } = await supabase
        .from("conceptos_entrada")
        .select("id")
        .or("nombre.eq.Honorarios por Adicionales,nombre.eq.Honorarios por adicionales,nombre.ilike.%honorarios%adicional%")
        .single();

      let totalHonorariosPagados = 0;
      console.log("🔍 DEBUG - Concepto encontrado:", conceptosEntrada);
      
      if (conceptosEntrada?.id) {
        const { data: honorariosPagados, error: errorHonorariosPagados } = await supabase
          .from("entradas")
          .select("monto, moneda, tipo_cambio, fecha")
          .eq("constructora_id", constructora.id)
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq('activo', true)
          .is('deleted_at', null)
          .eq("concepto_id", conceptosEntrada.id);

        console.log("🔍 DEBUG - Honorarios pagados encontrados:", honorariosPagados);

        if (!errorHonorariosPagados && honorariosPagados) {
          if (seActualiza && metodoActualizacion) {
            // CON CAC: Calcular UNIDADES pagadas (igual que HonorariosMO)
            let unidadesPagadas = 0;
            
            for (const pago of honorariosPagados) {
              // 1. Convertir pago a moneda de honorarios
              const montoEnMonedaHonorarios = convertirMonto(
                pago.monto,
                pago.moneda,
                monedaHonorarios,
                pago.tipo_cambio
              );
              // 2. Obtener índice del mes del pago
              const fechaPago = pago.fecha.substring(0, 7) + "-01";
              const indicePago = await obtenerIndiceAdicionales(fechaPago, metodoActualizacion, "adicionales");
              // 3. Calcular unidades pagadas
              if (indicePago) {
                const unidadesPago = montoEnMonedaHonorarios / indicePago;
                unidadesPagadas += unidadesPago;
              }
            }
            
            // 4. Convertir unidades pagadas a pesos con el último índice
            const fechaHoy = new Date().toISOString().substring(0, 7) + "-01";
            const ultimoIndice = await obtenerIndiceAdicionales(fechaHoy, metodoActualizacion, "adicionales");
            
            if (ultimoIndice) {
              const pagadosEnMonedaHonorarios = unidadesPagadas * ultimoIndice;
              totalHonorariosPagados = convertirMonto(pagadosEnMonedaHonorarios, monedaHonorarios, monedaVista, cotizacionDolar);
            }
          } else {
            // Sin CAC: suma simple
            honorariosPagados.forEach((entrada) => {
              totalHonorariosPagados += convertirMonto(
                entrada.monto,
                entrada.moneda,
                monedaVista,
                entrada.tipo_cambio
              );
            });
          }
        }
      }

      const datosAdicionales = {
        totalGastosAdicionales,
        honorariosCalculados,
        honorariosPagados: totalHonorariosPagados,
        honorariosPendientes: honorariosCalculados - totalHonorariosPagados,
        tieneAdicionales: totalGastosAdicionales > 0,
        honorariosHabilitados: proyectoSeleccionado.adicionales_honorarios_habilitado
      };

      setDatosAdicionales(datosAdicionales);
    } catch (err) {
      console.error("Error cargando datos de adicionales:", err);
      setDatosAdicionales(null);
    }
  };

  const cargarDistribucionGastos = async () => {
    try {
      const { data: entradas, error: errorEntradas } = await supabase
        .from("entradas")
        .select("monto, moneda, tipo_cambio, conceptos_entrada!inner(nombre)")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null);

      const distribucion = [];
      const conceptosMap = {};

      if (!errorEntradas && entradas) {
        entradas.forEach((entrada) => {
          const concepto = entrada.conceptos_entrada.nombre;
          const conceptoLower = concepto.toLowerCase();
          
          // SIEMPRE excluir comisiones para todos los usuarios
          const esComision = 
            conceptoLower.includes("comisión") ||
            conceptoLower.includes("comision");
          
          if (esComision) {
            return; // Saltar esta entrada
          }
          
          // Si es cliente, filtrar solo conceptos permitidos adicionales
          if (datosCliente?.esCliente) {
            const esConceptoPermitido = 
              conceptoLower.includes("adelanto de caja") ||
              conceptoLower.includes("adelanto caja") ||
              conceptoLower.includes("adelanto mo") ||
              conceptoLower.includes("adelanto mano de obra") ||
              conceptoLower.includes("honorario") ||
              conceptoLower.includes("honorarios");
            
            // Excluir otros conceptos no permitidos para clientes
            const esConceptoNoPermitido = 
              conceptoLower.includes("transferencia") ||
              conceptoLower.includes("pago") ||
              conceptoLower.includes("cobro");
            
            if (!esConceptoPermitido || esConceptoNoPermitido) {
              return; // Saltar esta entrada
            }
          }

          const montoConvertido = convertirMonto(
            entrada.monto,
            entrada.moneda,
            monedaVista,
            entrada.tipo_cambio
          );

          if (!conceptosMap[concepto]) {
            conceptosMap[concepto] = 0;
          }
          conceptosMap[concepto] += montoConvertido;
        });

        const colores = [
          "#3b82f6",
          "#10b981",
          "#f59e0b",
          "#ef4444",
          "#8b5cf6",
          "#06b6d4",
        ];
        let colorIndex = 0;

        Object.entries(conceptosMap).forEach(([concepto, monto]) => {
          if (monto > 0) {
            distribucion.push({
              name: concepto,
              value: monto,
              color: colores[colorIndex % colores.length],
            });
            colorIndex++;
          }
        });
      }

      setDistribucionGastos(distribucion);
    } catch (err) {
      console.error("Error cargando distribución de gastos:", err);
    }
  };

  const cargarGastosMensuales = async () => {
    try {
      const gastosPorMes = {};

      // 1. Honorarios
      const { data: honorarios, error: errorHonorarios } = await supabase
        .from("entradas")
        .select(
          "fecha, monto, moneda, tipo_cambio, conceptos_entrada!inner(nombre)"
        )
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .ilike("conceptos_entrada.nombre", "%honorario%")
        .eq('activo', true)
        .is('deleted_at', null);

      if (!errorHonorarios && honorarios) {
        honorarios.forEach((entrada) => {
          const mes = entrada.fecha.substring(0, 7);
          const concepto = "Honorarios";
          const montoConvertido = convertirMonto(
            entrada.monto,
            entrada.moneda,
            monedaVista,
            entrada.tipo_cambio
          );

          if (!gastosPorMes[mes]) gastosPorMes[mes] = {};
          if (!gastosPorMes[mes][concepto]) gastosPorMes[mes][concepto] = 0;

          gastosPorMes[mes][concepto] += montoConvertido;
        });
      }

      // 2. Mano de obra
      const { data: manoObra, error: errorMO } = await supabase
        .from("entradas")
        .select("fecha, monto, moneda, tipo_cambio, caja_destino_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq('activo', true)
        .is('deleted_at', null);

      let entradasManoObra = [];
      if (!errorMO && manoObra && manoObra.length > 0) {
        const { data: cajasManoObra } = await supabase
          .from("cajas")
          .select("id")
          .eq("constructora_id", constructora.id)
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo_caja", "mano_obra");

        const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];
        entradasManoObra = manoObra.filter((entrada) =>
          idsCajasManoObra.includes(entrada.caja_destino_id)
        );
      }

      if (entradasManoObra.length > 0) {
        entradasManoObra.forEach((entrada) => {
          const mes = entrada.fecha.substring(0, 7);
          const concepto = "Mano de Obra";
          const montoConvertido = convertirMonto(
            entrada.monto,
            entrada.moneda,
            monedaVista,
            entrada.tipo_cambio
          );

          if (!gastosPorMes[mes]) gastosPorMes[mes] = {};
          if (!gastosPorMes[mes][concepto]) gastosPorMes[mes][concepto] = 0;

          gastosPorMes[mes][concepto] += montoConvertido;
        });
      }

      // 3. Pagos Cliente Directo
      const { data: pagosCliente, error: errorPagosCliente } = await supabase
        .from("salidas")
        .select("fecha, monto, moneda, tipo_cambio")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .is("caja_origen_id", null);

      if (!errorPagosCliente && pagosCliente) {
        pagosCliente.forEach((salida) => {
          const mes = salida.fecha.substring(0, 7);
          const concepto = "Pagos Cliente";
          const montoConvertido = convertirMonto(
            salida.monto,
            salida.moneda,
            monedaVista,
            salida.tipo_cambio
          );

          if (!gastosPorMes[mes]) gastosPorMes[mes] = {};
          if (!gastosPorMes[mes][concepto]) gastosPorMes[mes][concepto] = 0;

          gastosPorMes[mes][concepto] += montoConvertido;
        });
      }

      // 4. Pagos desde Cajas
      const { data: pagosCajas, error: errorPagosCajas } = await supabase
        .from("salidas")
        .select("fecha, monto, moneda, tipo_cambio")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .not("caja_origen_id", "is", null);

      if (!errorPagosCajas && pagosCajas) {
        pagosCajas.forEach((salida) => {
          const mes = salida.fecha.substring(0, 7);
          const concepto = "Pagos desde Cajas";
          const montoConvertido = convertirMonto(
            salida.monto,
            salida.moneda,
            monedaVista,
            salida.tipo_cambio
          );

          if (!gastosPorMes[mes]) gastosPorMes[mes] = {};
          if (!gastosPorMes[mes][concepto]) gastosPorMes[mes][concepto] = 0;

          gastosPorMes[mes][concepto] += montoConvertido;
        });
      }

      // Convertir a array para recharts
      const mesesConDatos = Object.keys(gastosPorMes).sort();
      const todosLosConceptos = new Set();

      Object.values(gastosPorMes).forEach((mes) => {
        Object.keys(mes).forEach((concepto) => todosLosConceptos.add(concepto));
      });

      if (mesesConDatos.length > 0) {
        const primerMes = mesesConDatos[0];
        const ultimoMes = mesesConDatos[mesesConDatos.length - 1];

        const mesesCompletos = [];
        let [year, month] = primerMes.split("-").map(Number);
        const [endYear, endMonth] = ultimoMes.split("-").map(Number);

        while (year < endYear || (year === endYear && month <= endMonth)) {
          const mesString = `${year}-${month.toString().padStart(2, "0")}`;
          mesesCompletos.push(mesString);

          month++;
          if (month > 12) {
            month = 1;
            year++;
          }
        }

        const datosGrafico = mesesCompletos.map((mes) => {
          const datos = { mes: formatMonthYear(mes), mesRaw: mes };

          Array.from(todosLosConceptos).forEach((concepto) => {
            datos[concepto] = gastosPorMes[mes]?.[concepto] || 0;
          });

          return datos;
        });

        setGastosMensuales(datosGrafico);
      } else {
        setGastosMensuales([]);
      }
    } catch (err) {
      console.error("Error cargando gastos mensuales:", err);
    }
  };

  const formatCurrency = (valor, monedaEspecifica = null) => {
    // Evitar mostrar símbolo incongruente mientras se recalcula
    if (!monedaEspecifica && recalculandoMoneda) return '...';
    // Si se especifica una moneda, usar esa; si no, usar la moneda efectiva
    const monedaAUsar = monedaEspecifica || monedaCalculos;
    const simbolo = monedaAUsar === "ARS" ? "$" : "US$";
    return `${simbolo}${valor.toLocaleString("es-AR", {
      maximumFractionDigits: 2,
    })}`;
  };

  const getColoresGrafico = () => {
    return {
      Honorarios: "#3b82f6",
      "Mano de Obra": "#10b981",
      "Pagos Cliente": "#f59e0b",
      "Pagos desde Cajas": "#ef4444",
    };
  };

  // Preparar opciones para el dropdown
  const opcionesProyectos = proyectos.map((proyecto) => ({
    value: proyecto.id,
    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
      proyecto.obra_finalizada ? " (Finalizado)" : ""
    }`,
  }));

  // Pantalla de carga mientras verifica permisos
  if (verificandoPermisos) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "#64748b",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            background: colorVista.gradient,
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "24px",
          }}
        >
          <Loader2 size={32} color="white" className="loading-pulse" />
        </div>
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "600",
            color: "#1e293b",
            marginBottom: "8px",
            margin: 0,
          }}
        >
          Verificando permisos
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "#64748b",
            margin: 0,
          }}
        >
          Preparando acceso al dashboard...
        </p>
      </div>
    );
  }

  // Mostrar error si el cliente no puede acceder
  if (datosCliente?.esCliente && datosCliente?.error) {
    return (
      <div
        style={{
          padding: isMobile ? "20px" : "40px",
          textAlign: "center",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "48px",
            borderRadius: "20px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px solid #fecaca",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>🚫</div>
          <h3 style={{ color: "#dc2626", marginBottom: "10px" }}>
            Acceso Denegado
          </h3>
          <p style={{ color: "#ef4444", marginBottom: "20px" }}>
            {datosCliente.error}
          </p>
          <p style={{ color: "#64748b", fontSize: "14px" }}>
            Contacta a tu constructora si crees que esto es un error.
          </p>
        </div>
      </div>
    );
  }

  // Componente de Skeleton para el Dashboard
  const DashboardSkeleton = () => (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header Skeleton */}
      <div
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                // Usar azul de marca para evitar flash celeste en carga
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px #2563eb40",
              }}
            >
              <BarChart3 size={24} color="white" />
            </div>
            <div>
              <div
                style={{
                  width: "120px",
                  height: "24px",
                  background:
                    "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  borderRadius: "6px",
                  animation: "shimmer 1.5s infinite",
                }}
              />
              <div
                style={{
                  width: "80px",
                  height: "16px",
                  background:
                    "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  borderRadius: "4px",
                  marginTop: "4px",
                  animation: "shimmer 1.5s infinite 0.2s",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div
              style={{
                width: "200px",
                height: "44px",
                background:
                  "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                borderRadius: "12px",
                animation: "shimmer 1.5s infinite 0.4s",
              }}
            />
            <div
              style={{
                width: "80px",
                height: "44px",
                background:
                  "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                borderRadius: "12px",
                animation: "shimmer 1.5s infinite 0.6s",
              }}
            />
          </div>
        </div>
      </div>

      {/* Grid de Cards Skeleton */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        {/* Card 1 */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "12px",
              marginBottom: "16px",
              animation: "shimmer 1.5s infinite",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "24px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "6px",
              marginBottom: "8px",
              animation: "shimmer 1.5s infinite 0.2s",
            }}
          />
          <div
            style={{
              width: "80%",
              height: "32px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "8px",
              animation: "shimmer 1.5s infinite 0.4s",
            }}
          />
        </div>

        {/* Card 2 */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "12px",
              marginBottom: "16px",
              animation: "shimmer 1.5s infinite 0.3s",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "24px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "6px",
              marginBottom: "8px",
              animation: "shimmer 1.5s infinite 0.5s",
            }}
          />
          <div
            style={{
              width: "80%",
              height: "32px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "8px",
              animation: "shimmer 1.5s infinite 0.7s",
            }}
          />
        </div>

        {/* Card 3 */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "12px",
              marginBottom: "16px",
              animation: "shimmer 1.5s infinite 0.6s",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "24px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "6px",
              marginBottom: "8px",
              animation: "shimmer 1.5s infinite 0.8s",
            }}
          />
          <div
            style={{
              width: "80%",
              height: "32px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "8px",
              animation: "shimmer 1.5s infinite 1s",
            }}
          />
        </div>
      </div>

      {/* Gráficos Skeleton */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            minHeight: "300px",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "24px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "6px",
              marginBottom: "20px",
              animation: "shimmer 1.5s infinite",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "200px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "12px",
              animation: "shimmer 1.5s infinite 0.3s",
            }}
          />
        </div>

        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            minHeight: "300px",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "24px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "6px",
              marginBottom: "20px",
              animation: "shimmer 1.5s infinite 0.6s",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "200px",
              background:
                "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              borderRadius: "12px",
              animation: "shimmer 1.5s infinite 0.9s",
            }}
          />
        </div>
      </div>
    </div>
  );

  // 🎉 MOSTRAR PÁGINA DE ÉXITO DE SUSCRIPCIÓN
  if (showSubscriptionSuccess) {
    return (
      <SubscriptionSuccess
        user={usuario}
        onContinueToOnboarding={handleContinueToOnboarding}
      />
    );
  }

  // Mostrar skeleton mientras carga
  if (loading) {
    return <DashboardSkeleton />;
  }

  // Mostrar mensaje cuando no hay proyectos disponibles
  if (!proyectoSeleccionado && proyectos.length === 0) {
    return (
      <div
        style={{
          padding: isMobile ? "20px" : "40px",
          textAlign: "center",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "48px",
            borderRadius: "20px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              background: colorVista.gradient,
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Building2 size={40} color="white" />
          </div>
          <h3
            style={{
              color: "#1e293b",
              marginBottom: "12px",
              fontSize: "20px",
              fontWeight: "600",
              margin: "0 0 12px 0",
            }}
          >
            No hay proyectos disponibles
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              lineHeight: "1.5",
              margin: 0,
            }}
          >
            {datosCliente?.esCliente
              ? "No se encontró un proyecto asociado a tu email. Contacta a tu constructora."
              : "Crea tu primer proyecto para comenzar a visualizar el dashboard con métricas y análisis detallados."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO SIGUIENDO LOS LINEAMIENTOS */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px #2563eb40",
                flexShrink: 0,
              }}
            >
              <BarChart3 size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Dashboard de proyecto
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Análisis completo del estado financiero y progreso
              </p>
            </div>
          </div>

          {/* Toggle de moneda */}
          <CurrencyToggle value={monedaVista} onChange={setMonedaVista} />
        </div>

        {/* Selector de proyecto - SOLO MOSTRAR SI NO ES CLIENTE O SI ES EMPLEADO */}
        {(!datosCliente?.esCliente || proyectos.length > 1) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ position: "relative" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                PROYECTO SELECCIONADO
                {datosCliente?.esCliente && (
                  <span
                    style={{
                      color: "#64748b",
                      fontWeight: "400",
                      marginLeft: "8px",
                    }}
                  >
                    (Tu proyecto)
                  </span>
                )}
              </label>
              <CustomDropdown
                value={proyectoSeleccionado?.id || ""}
                onChange={(proyectoId) => {
                  // Solo permitir cambio si no es cliente
                  if (!datosCliente?.esCliente) {
                    const proyecto = proyectos.find((p) => p.id === proyectoId);
                    setProyectoSeleccionado(proyecto);

                    // Guardar la selección en localStorage
                    localStorage.setItem(
                      `dashboard_proyecto_${constructora.id}`,
                      proyectoId
                    );
                    console.log("💾 GUARDANDO SELECCIÓN:", proyecto.codigo);
                  }
                }}
                options={opcionesProyectos}
                placeholder="Selecciona un proyecto"
                disabled={datosCliente?.esCliente || loadingData}
                icon={Building2}
              />
              {datosCliente?.esCliente && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Como cliente, solo puedes ver tu proyecto asignado
                </p>
              )}
            </div>
            <div></div>
          </div>
        )}
      </div>

      {/* ✅ STATS COMPACTAS SIGUIENDO LOS LINEAMIENTOS - SOLO DESKTOP */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {[
            {
              label: "Total Gastado",
              value:
                loading || loadingData ? "..." : formatCurrency(gastoTotal),
              icon: DollarSign,
              color: "#ef4444",
              isText: false,
            },
            {
              label: "Metros Cómputo",
              value:
                loading || loadingData
                  ? "..."
                  : `${infoObra?.metrosComputo || 0} m²`,
              icon: Ruler,
              color: "#10b981",
              isText: true,
            },
            {
              label: "Tiempo Transcurrido",
              value:
                loading || loadingData
                  ? "..."
                  : infoObra?.tiempoTranscurrido || "No definido",
              icon: Clock,
              color: "#f59e0b",
              isText: true,
            },
            {
              label: "Finalización Estimada",
              value:
                loading || loadingData
                  ? "..."
                  : fechaFinalizacionCronograma
                  ? fechaFinalizacionCronograma.toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                    })
                  : "Sin cronograma",
              icon:
                loading || loadingData
                  ? Activity
                  : fechaFinalizacionCronograma
                  ? CalendarCheck
                  : AlertTriangle,
              color:
                loading || loadingData
                  ? "#64748b"
                  : fechaFinalizacionCronograma
                  ? "#10b981"
                  : "#f59e0b",
              isText: true,
            },
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: stat.isText ? "11px" : "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                    lineHeight: "1.2",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stat.value}
                </p>
                {/* ✅ MOSTRAR DURACIÓN TOTAL en tarjeta de finalización */}
                {stat.label === "Finalización Estimada" &&
                  duracionTotalProyecto && (
                    <p
                      style={{
                        fontSize: "9px",
                        color: "#94a3b8",
                        margin: "2px 0 0 0",
                        fontWeight: "500",
                      }}
                    >
                      {duracionTotalProyecto}
                    </p>
                  )}
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ CARDS PRINCIPALES - LAYOUT ORIGINAL RESTAURADO */}
      <div
        className="main-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        {/* Saldo Disponible */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: "slideInRight 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#10b98110",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Wallet size={16} color="#10b981" />
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Caja disponible
            </h3>
          </div>

          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            <div
              onClick={() => navegarAFlujoCaja('ARS')}
              style={{
                background: "#f8fafc",
                padding: "12px",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f1f5f9";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#f8fafc";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "500",
                }}
              >
                PESOS ARGENTINOS
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1e293b",
                }}
              >
                {loading || loadingData
                  ? "..."
                  : `$${dineroCajas.ARS.toLocaleString("es-AR")}`}
              </div>
            </div>

            <div
              onClick={() => navegarAFlujoCaja('USD')}
              style={{
                background: "#f8fafc",
                padding: "12px",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f1f5f9";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#f8fafc";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "500",
                }}
              >
                DÓLARES ESTADOUNIDENSES
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1e293b",
                }}
              >
                {loading || loadingData
                  ? "..."
                  : `US$${dineroCajas.USD.toLocaleString("es-AR")}`}
              </div>
            </div>
          </div>
        </div>

        {/* **MODIFICADO** Nuevo Gauge Chart - Costo vs Presupuesto */}
        {(() => {
          const { presupuestoInicialPorMetro, presupuestoActualizadoPorMetro } =
            calcularPresupuestosPorMetro();

          return (
            <ModernGaugeChart
              costoActualTotal={gastoTotal}
              costoActualPorMetro={gastoPorMetro}
              presupuestoInicialTotal={totalPresupuestadoInicial}
              presupuestoInicialPorMetro={presupuestoInicialPorMetro}
              presupuestoActualizadoTotal={totalPresupuestadoActualizado}
              presupuestoActualizadoPorMetro={presupuestoActualizadoPorMetro}
              metrosComputo={infoObra?.metrosComputo || 0}
              formatCurrency={formatCurrency}
              monedaVista={monedaVista}
              isMobile={isMobile}
              datosCompletamenteCargados={datosCompletamenteCargados}
            />
          );
        })()}

        {/* ✅ NUEVA SUBTARJETA: Gastos Adicionales */}
        {datosAdicionales?.tieneAdicionales && (
          <div
            className="card-hover"
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "20px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: "slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
              marginTop: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#f59e0b10",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Package size={16} color="#f59e0b" />
              </div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: 0,
                }}
                >
                  Gastos adicionales
                </h3>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "12px",
                gap: "16px",
              }}
            >
              <div style={{ textAlign: "center", flex: 1 }}>
                <p
                  style={{
                    margin: "0 0 2px 0",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  Total
                </p>
                <p
                  style={{
                    margin: 0,
                    color: "#1e293b",
                    fontWeight: "700",
                    fontSize: "14px",
                  }}
                >
                  {formatCurrency(Math.round(datosAdicionales.totalGastosAdicionales + (datosAdicionales.honorariosHabilitados ? datosAdicionales.honorariosPagados : 0)))}
                </p>
              </div>
              <div style={{ textAlign: "center", flex: 1 }}>
                <p
                  style={{
                    margin: "0 0 2px 0",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  Gastos
                </p>
                <p
                  style={{
                    margin: 0,
                    color: "#f59e0b",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  {formatCurrency(Math.round(datosAdicionales.totalGastosAdicionales))}
                </p>
              </div>
              {datosAdicionales.honorariosHabilitados && (
                <div style={{ textAlign: "center", flex: 1 }}>
                  <p
                    style={{
                      margin: "0 0 2px 0",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Honorarios
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "#3b82f6",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {formatCurrency(Math.round(datosAdicionales.honorariosPagados))}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contenedor para Información del Proyecto e Indicadores Fiscales */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {/* Información del Proyecto */}
          <div
            className="card-hover"
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "16px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: "slideInRight 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#3b82f610",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={16} color="#3b82f6" />
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Información del proyecto
            </h3>
          </div>

          {infoObra && (
            <div style={{ display: "grid", gap: "12px" }}>
              <div
                style={{
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    marginBottom: "4px",
                  }}
                >
                  {infoObra.apellidoCliente}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <MapPin size={12} />
                  {infoObra.ubicacion || "Ubicación no especificada"}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "8px",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#1e293b",
                      marginBottom: "2px",
                    }}
                  >
                    {infoObra.metrosComputo}
                  </div>
                  <div style={{ fontSize: "10px", color: "#64748b" }}>
                    m² cómputo
                  </div>
                </div>

                {/* ✅ NUEVA INFO: Duración total del cronograma */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "8px",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: fechaFinalizacionCronograma
                        ? "#10b981"
                        : "#f59e0b",
                      marginBottom: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    {fechaFinalizacionCronograma ? (
                      <>
                        <CalendarCheck size={12} />
                        Cronograma
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={12} />
                        Sin cronograma
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: "10px", color: "#64748b" }}>
                    {duracionTotalProyecto || "No definido"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Indicadores Fiscales */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "16px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: "slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            marginTop: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                background: "#ef444410",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart3 size={14} color="#ef4444" />
            </div>
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Indicadores fiscales
            </h3>
          </div>

          <div style={{ display: "grid", gap: "10px" }}>
            <div
              style={{
                background: "#fef2f2",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #fecaca",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#991b1b",
                  marginBottom: "3px",
                }}
              >
                IVA DEDUCIBLE (Facturas A)
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#dc2626",
                }}
              >
                ${Math.round(indicadoresFiscales.ivaDeducible).toLocaleString()}
              </div>
            </div>

            <div
              style={{
                background: "#f0fdf4",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #bbf7d0",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#166534",
                  marginBottom: "3px",
                }}
              >
                AHORRO ESTIMADO GANANCIAS 30% (A, B, C)
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#16a34a",
                }}
              >
                ${Math.round(indicadoresFiscales.ahorroEstimado).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* ✅ NUEVA SECCIÓN: Grid reorganizado con diseño desequilibrado */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        {/* Estado Financiero - MÁS ANCHO */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: "slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#7c3aed10",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Target size={16} color="#7c3aed" />
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Ejecución presupuestaria
            </h3>
            <p
              style={{
                fontSize: "12px",
                color: "#64748b",
                margin: "2px 0 0 0",
                fontStyle: "italic",
              }}
            >
              sin adicionales
            </p>
          </div>

          {loading || loadingData ? (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #e2e8f0",
                  borderTop: "3px solid #7c3aed",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "12px",
                }}
              ></div>
              <p style={{ margin: 0, textAlign: "center" }}>
                Cargando estado financiero...
              </p>
            </div>
          ) : estadoFinancieroListo ? (
            <EstadoFinancieroChart
              datosFinancieros={datosFinancieros}
              totalPresupuestadoActualizado={totalPresupuestadoActualizado}
              formatCurrency={formatCurrency}
              isMobile={isMobile}
            />
          ) : (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #e2e8f0",
                  borderTop: "3px solid #7c3aed",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "12px",
                }}
              />
              <p style={{ margin: 0, textAlign: "center" }}>
                Cargando estado financiero...
              </p>
            </div>
          )}
        </div>

        {/* Solo mostrar honorarios si hay proyecto seleccionado y datos válidos */}
        {proyectoSeleccionado && (
          <HonorariosManoObraCard
            datosHonorarios={datosHonorarios}
            datosManoObra={datosManoObra}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            proyectoSeleccionado={proyectoSeleccionado}
            datosAdicionales={datosAdicionales}
          />
        )}

      </div>

      {/* ✅ GRÁFICOS REORGANIZADOS - DISEÑO DESEQUILIBRADO */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr",
          gap: "20px",
          marginBottom: "24px",
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Distribución de gastos - MÁS ESTRECHO */}
        <div
          className="card-hover"
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "transparent",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PieChartIcon size={16} color="#0ea5e9" />
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Distribución de pagos hechos a la constructora
            </h3>
          </div>

          {loading || loadingData ? (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #e2e8f0",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "12px",
                }}
              ></div>
              <p style={{ margin: 0, textAlign: "center" }}>
                Cargando distribución...
              </p>
            </div>
          ) : distribucionGastos.length > 0 ? (
            <div style={{ height: "280px" }}>
              <div
                style={{
                  height: "180px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "180px",
                    height: "180px",
                    position: "relative",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distribucionGastos}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                      >
                        {distribucionGastos.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        position={{ x: 0, y: -20 }}
                        formatter={(value, name) => {
                          const total = distribucionGastos.reduce(
                            (sum, item) => sum + item.value,
                            0
                          );
                          const percentage = ((value / total) * 100).toFixed(1);
                          return [
                            formatCurrency(value) + ` (${percentage}%)`,
                            name,
                          ];
                        }}
                        contentStyle={{
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          padding: "8px 12px",
                          fontSize: "12px",
                        }}
                        labelStyle={{
                          color: "#1e293b",
                          fontWeight: "600",
                          marginBottom: "4px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div
                style={{
                  textAlign: "center",
                  marginBottom: "12px",
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    marginBottom: "4px",
                    fontWeight: "500",
                    letterSpacing: "0.5px",
                  }}
                >
                  TOTAL ACUMULADO
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                  }}
                >
                  {formatCurrency(
                    distribucionGastos.reduce(
                      (sum, item) => sum + item.value,
                      0
                    )
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  justifyContent: "center",
                }}
              >
                {distribucionGastos.map((item, index) => {
                  const total = distribucionGastos.reduce(
                    (sum, cat) => sum + cat.value,
                    0
                  );
                  const percentage = ((item.value / total) * 100).toFixed(1);
                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 8px",
                        background: "#f8fafc",
                        borderRadius: "6px",
                        border: `1px solid ${item.color}20`,
                        fontSize: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "2px",
                          background: item.color,
                          flexShrink: 0,
                        }}
                      ></div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        {item.name}
                      </span>
                      <span
                        style={{
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        {percentage}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#f1f5f9",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "12px",
                }}
              >
                <FileText size={24} style={{ opacity: 0.5 }} />
              </div>
              <h4
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                Sin datos disponibles
              </h4>
              <p
                style={{
                  margin: 0,
                  textAlign: "center",
                  lineHeight: "1.4",
                  fontSize: "12px",
                }}
              >
                No hay información de entradas para mostrar
              </p>
            </div>
          )}
        </div>

        {/* Gastos mensuales - MÁS ANCHO */}
        <div
          className="card-hover"
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#3b82f610",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Calendar size={16} color="#3b82f6" />
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
              }}
            >
              Evolución mensual de gastos
            </h3>
          </div>

          {loading || loadingData ? (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #e2e8f0",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "12px",
                }}
              ></div>
              <p style={{ margin: 0, textAlign: "center" }}>
                Cargando evolución...
              </p>
            </div>
          ) : gastosMensuales.length > 0 ? (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  marginBottom: "12px",
                  padding: "8px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                }}
              >
                {gastosMensuales.length > 0 &&
                  Object.keys(gastosMensuales[0])
                    .filter((key) => key !== "mes" && key !== "mesRaw")
                    .filter((concepto) => {
                      return gastosMensuales.some((mes) => mes[concepto] > 0);
                    })
                    .map((concepto) => {
                      const colores = getColoresGrafico();
                      return (
                        <div
                          key={concepto}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "2px",
                              background: colores[concepto] || "#64748b",
                            }}
                          ></div>
                          <span
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            {concepto}
                          </span>
                        </div>
                      );
                    })}
              </div>

              {/* ✅ GRÁFICO OPTIMIZADO PARA OCUPAR MEJOR EL ESPACIO */}
              <div style={{ flex: 1, minHeight: "220px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={gastosMensuales}
                    margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="mes"
                      stroke="#64748b"
                      fontSize={10}
                      angle={-45}
                      textAnchor="end"
                      height={36}
                      tickMargin={6}
                      tick={{ dy: 6 }}
                      fontWeight="500"
                    />
                    <YAxis
                      tickFormatter={(value) => formatCurrency(value)}
                      stroke="#64748b"
                      fontSize={10}
                      fontWeight="500"
                    />
                    <Tooltip
                      position={{ x: 0, y: -20 }}
                      formatter={(value, name, props) => {
                        const mesData = props.payload;
                        const totalMes = Object.keys(mesData)
                          .filter((key) => key !== "mes" && key !== "mesRaw")
                          .reduce((sum, key) => sum + (mesData[key] || 0), 0);

                        const porcentaje =
                          totalMes > 0
                            ? ((value / totalMes) * 100).toFixed(1)
                            : "0.0";

                        return [
                          `${formatCurrency(value)} (${porcentaje}%)`,
                          name,
                        ];
                      }}
                      labelStyle={{ color: "#1e293b", fontWeight: "600" }}
                      contentStyle={{
                        background: "white",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        fontSize: "12px",
                      }}
                    />
                    {gastosMensuales.length > 0 &&
                      Object.keys(gastosMensuales[0])
                        .filter((key) => key !== "mes" && key !== "mesRaw")
                        .filter((concepto) => {
                          return gastosMensuales.some(
                            (mes) => mes[concepto] > 0
                          );
                        })
                        .map((concepto, index, filteredArray) => {
                          const colores = getColoresGrafico();
                          const isFirst = index === 0;
                          const isLast = index === filteredArray.length - 1;

                          let radius = [0, 0, 0, 0];
                          if (isFirst && isLast) {
                            radius = [4, 4, 4, 4];
                          } else if (isFirst) {
                            radius = [0, 0, 4, 4];
                          } else if (isLast) {
                            radius = [4, 4, 0, 0];
                          }

                          return (
                            <Bar
                              key={concepto}
                              dataKey={concepto}
                              stackId="gastos"
                              fill={colores[concepto] || "#64748b"}
                              radius={radius}
                            />
                          );
                        })}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div
              style={{
                height: "280px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#f1f5f9",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "12px",
                }}
              >
                <Calendar size={24} style={{ opacity: 0.5 }} />
              </div>
              <h4
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                Sin actividad mensual
              </h4>
              <p
                style={{
                  margin: 0,
                  textAlign: "center",
                  lineHeight: "1.4",
                  fontSize: "12px",
                }}
              >
                No hay datos de gastos mensuales para visualizar
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS Y EFECTOS HOVER */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* ✅ EFECTOS HOVER MEJORADOS - MÁS PROFESIONALES */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }

        /* Mejorar scrollbars en webkit browsers */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Transiciones suaves para elementos interactivos */
        button {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        /* Mejorar el aspecto de los gráficos en dispositivos de alta densidad */
        .recharts-wrapper {
          font-family: inherit;
        }

        .recharts-cartesian-axis-tick-value {
          font-weight: 500;
        }

        .recharts-tooltip-wrapper {
          outline: none;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .recharts-cartesian-axis-tick-value {
            font-size: 10px;
          }

          .recharts-tooltip-wrapper {
            font-size: 11px;
          }
        }

        /* Focus states mejorados para accesibilidad */
        button:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Mejoras para el dropdown personalizado */
        .dropdown-option:focus {
          background: #f1f5f9 !important;
          outline: none;
        }

        /* ✅ MEJORAS ESPECÍFICAS PARA TOOLTIPS DE RECHARTS */
        .recharts-tooltip-content {
          background: white !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          padding: 8px 12px !important;
          color: #1e293b !important;
          font-size: 12px !important;
        }

        .recharts-tooltip-label {
          color: #1e293b !important;
          font-weight: 600 !important;
          margin-bottom: 4px !important;
        }

        /* ✅ OPTIMIZACIONES PARA GRÁFICOS EN MÓVIL */
        @media (max-width: 768px) {
          .recharts-cartesian-grid line {
            stroke-dasharray: 2 2;
            opacity: 0.5;
          }

          .recharts-bar {
            transition: all 0.2s ease-in-out;
          }

          .card-hover:hover {
            transform: none;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
          }
        }

        /* ✅ MEJORAS PARA LA NUEVA TARJETA DE ESTADO FINANCIERO */
        .recharts-pie-sector:hover {
          filter: brightness(1.1);
          transition: filter 0.2s ease;
        }

        /* Asegurar que los tooltips del donut sean legibles */
        .recharts-tooltip-wrapper .recharts-tooltip-content {
          background: white !important;
          border: 1px solid #e2e8f0 !important;
          color: #1e293b !important;
        }

        /* Animación suave para carga de datos */
        .loading-container {
          animation: fadeIn 0.3s ease-in-out;
        }

        /* Mejorar la apariencia de las leyendas */
        .legend-item {
          transition: all 0.2s ease;
        }

        .legend-item:hover {
          transform: scale(1.05);
          background: #f1f5f9 !important;
        }

        /* Estilos para estados de error o sin datos */
        .empty-state {
          animation: fadeInUp 0.5s ease-in-out;
        }

        /* Optimización para pantallas de alta resolución */
        @media (min-resolution: 2dppx) {
          .recharts-surface {
            shape-rendering: geometricPrecision;
          }
        }

        /* ✅ ESTILOS ESPECÍFICOS PARA BARRAS DE PROGRESO REDONDEADAS */
        .progress-bar {
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .progress-bar::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 6px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 0 20px 20px 0;
        }

        /* ✅ EFECTOS HOVER PARA TARJETAS DE HONORARIOS Y MANO DE OBRA */
        .progress-container:hover .progress-bar {
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        }

        /* ✅ MEJORAS PARA TOOLTIPS EN POSICIÓN SUPERIOR */
        .recharts-tooltip-wrapper {
          z-index: 1000;
        }

        /* ✅ ESTILOS PARA GRID DESEQUILIBRADO RESPONSIVE */
        @media (max-width: 768px) {
          .main-cards-grid {
            grid-template-columns: 1fr !important;
          }

          .charts-asymmetric-grid {
            grid-template-columns: 1fr !important;
          }

          .financial-overview-grid {
            grid-template-columns: 1fr !important;
          }
        }

        /* ✅ ANIMACIONES ESCALONADAS PARA ELEMENTOS */
        .card-hover:nth-child(1) {
          animation-delay: 0.1s;
        }

        .card-hover:nth-child(2) {
          animation-delay: 0.2s;
        }

        .card-hover:nth-child(3) {
          animation-delay: 0.3s;
        }

        /* ✅ MEJORAS PARA ACCESIBILIDAD EN ELEMENTOS INTERACTIVOS */
        .card-hover {
          cursor: default;
        }

        .card-hover:focus-within {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* ✅ ESTILOS PARA ICONOS CON MEJOR CONTRASTE */
        .icon-container {
          backdrop-filter: blur(8px);
        }

        /* ✅ MEJORAS PARA TEXTO TRUNCADO EN MÓVIL */
        @media (max-width: 480px) {
          .text-truncate-mobile {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 200px;
          }
        }

        /* ✅ ESTILOS PARA BADGES Y ETIQUETAS */
        .badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* ✅ MEJORAS PARA CONTENEDORES DE STATS */
        .stats-container {
          position: relative;
          overflow: hidden;
        }

        .stats-container::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          transition: left 0.5s;
        }

        .stats-container:hover::before {
          left: 100%;
        }

        /* ✅ ESTILOS FINALES PARA COMPONENTES CUSTOM */
        .custom-component {
          will-change: transform;
        }

        .loading-shimmer {
          background: linear-gradient(
            90deg,
            #f1f5f9 25%,
            #e2e8f0 50%,
            #f1f5f9 75%
          );
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        /* ✅ ANIMACIÓN SHIMMER PARA PLACEHOLDERS */
        .shimmer {
          background: linear-gradient(
            90deg,
            #f1f5f9 25%,
            #e2e8f0 50%,
            #f1f5f9 75%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}

export default Dashboard;

// ✅ ANIMACIONES CSS PARA SKELETON LOADING
const style = document.createElement("style");
style.textContent = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .loading-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: .5;
    }
  }
`;

// Solo agregar el estilo si no existe ya
if (!document.querySelector("#dashboard-skeleton-styles")) {
  style.id = "dashboard-skeleton-styles";
  document.head.appendChild(style);
}
